import {
  Box,
  useTheme,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Button,
  IconButton,
  MenuItem,
  Autocomplete,
  createFilterOptions,
  Divider,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  List,
  ListItemButton,
  CircularProgress,
  Popper,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVertRounded";
import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import api from "services/api";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import { TimeField } from "@mui/x-date-pickers";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { HtmlTooltip } from "components/HeatMap";
import { useAuth } from "../../../contexts/AuthContext";
import InlineGridSelector from "components/InlineGridSelector";
import UploadFileIcon from "@mui/icons-material/UploadFileRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import { getTextFieldBorderStyles } from "../../../theme";
import { usePageTitle } from "hooks/usePageTitle";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import { useWebSocket } from "contexts/WebSocketContext";
import { usePresence } from "contexts/PresenceContext";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("America/New_York");

export const getBorderColor = (
  theme,
  isNewAssignment,
  newData,
  oldData,
  optional = false,
) => {
  const flag = newData === null || newData === "";

  if (!isNewAssignment && newData !== oldData) {
    if (optional)
      return flag ? theme.palette.error.main : theme.palette.info[500];

    return theme.palette.info[400];
  } else {
    if (optional)
      return flag ? theme.palette.error.main : theme.palette.primary.main;

    return flag ? theme.palette.error.main : theme.palette.primary.main;
  }
};

// Helper function for time field button styling
const getTimeFieldButtonSx = (theme) => ({
  padding: "2px",
  bgcolor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "8px",
  "&:hover": {
    bgcolor: theme.palette.primary.main,
    color: theme.palette.background.default,
    border: `1px solid ${theme.palette.primary.main}`,
  },
  "&:disabled": {
    bgcolor: theme.palette.action.disabledBackground,
  },
});

// Helper function to create time field adornments with increment/decrement buttons
const createTimeFieldAdornments = (
  theme,
  disabled,
  onDecrement,
  onIncrement,
) => ({
  startAdornment: (
    <InputAdornment position="start">
      <IconButton
        size="small"
        disabled={disabled}
        onClick={onDecrement}
        sx={getTimeFieldButtonSx(theme)}
      >
        <RemoveRoundedIcon fontSize="small" />
      </IconButton>
    </InputAdornment>
  ),
  endAdornment: (
    <InputAdornment position="end">
      <IconButton
        size="small"
        disabled={disabled}
        onClick={onIncrement}
        sx={getTimeFieldButtonSx(theme)}
      >
        <AddRoundedIcon fontSize="small" />
      </IconButton>
    </InputAdornment>
  ),
});

const MENTION_TRIGGER_REGEX = /(^|\s)([@#])([a-zA-Z0-9._-]*)$/;

const getTagContextAtCursor = (text, cursorPosition) => {
  if (typeof text !== "string") return null;
  const safeCursor = typeof cursorPosition === "number" ? cursorPosition : text.length;
  const beforeCursor = text.slice(0, safeCursor);
  const match = beforeCursor.match(MENTION_TRIGGER_REGEX);
  if (!match) return null;
  return {
    trigger: match[2],
    query: (match[3] || "").trim(),
    cursorPosition: safeCursor,
  };
};

const getMemberMentionToken = (member) => {
  const nameToken = String(member?.Name || "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "");
  if (nameToken) return nameToken;
  const emailLocalPart = String(member?.Email || "")
    .split("@")[0]
    .trim()
    .toLowerCase()
    .replace(/[^a-zA-Z0-9._-]/g, "");
  if (emailLocalPart) return emailLocalPart;
  return "";
};

const InterpretationNewEdit = () => {
  const filter = createFilterOptions();

  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const { setCurrentEntity } = usePresence();

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayData, setOverlayName, triggerPageRefresh } =
    useStateContext();

  usePageTitle(
    overlayData.Action === OVERLAY_NAMES.INTERPRETATION_ACTION_NEW
      ? "New Interpretation"
      : "Edit Interpretation",
  );

  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  // State for paid assignment warning dialog
  const [showPaidWarning, setShowPaidWarning] = useState(false);
  const [paidWarningMessage, setPaidWarningMessage] = useState("");
  const [pendingSave, setPendingSave] = useState(false);

  const isNewAssignment =
    overlayData.Action === OVERLAY_NAMES.INTERPRETATION_ACTION_NEW;

  // Edit lock for existing services only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "service",
    isNewAssignment ? null : overlayData.id,
  );

  const [, setAddNewClient] = useState(false);
  const [, setAddNewRequestor] = useState(false);
  const [, setAddNewInvoiceTo] = useState(false);

  // Set entity tracking for presence and updates
  useEffect(() => {
    if (!isNewAssignment && overlayData.id) {
      setCurrentEntity({
        entityType: "Interpretation",
        entityId: overlayData.id,
      });
    }
    return () => {
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [isNewAssignment, overlayData.id, setCurrentEntity]);

  // Listen for assignment updates from other members
  const handleAssignmentRefetch = () => {
    // Reload the overlay data with fresh data from server
    api
      .get(`/services/interpretations/${overlayData.id}`)
      .then((response) => {
        setOverlayData({
          ...overlayData,
          ...response.data,
        });
        triggerPageRefresh("Assignment data refreshed", theme);
      })
      .catch((err) => console.log("Error refetching assignment:", err));
  };

  useAssignmentUpdates({
    modelType: "Interpretation",
    recordId: isNewAssignment ? null : overlayData.id,
    onUpdate: handleAssignmentRefetch,
  });

  const updateClientRate = (client, currentLangID) => {
    const clientRates = client?.ClientRates;
    if (!Array.isArray(clientRates) || clientRates.length === 0) return 0;

    return (
      clientRates.find((clientRate) => clientRate.Language.id === currentLangID)
        ?.Rate ||
      clientRates.find((clientRate) => clientRate.Language.id === 1)?.Rate ||
      0
    );
  };

  const completing =
    overlayData.Action === OVERLAY_NAMES.INTERPRETATION_ACTION_COMPLETE;

  const [name, setName] = useState(isNewAssignment ? "" : overlayData.Name);

  const [date, setDate] = useState(
    isNewAssignment
      ? overlayData.PrefilledData
        ? dayjs.utc(overlayData.PrefilledData.Date)
        : null
      : dayjs.utc(overlayData.Date),
  );

  // Holiday state for the selected date
  const [selectedDateHoliday, setSelectedDateHoliday] = useState(null);

  const [repeatUntilDate, setRepeatUntilDate] = useState(
    isNewAssignment || !overlayData.Date
      ? dayjs.utc().add(1, "month")
      : dayjs.utc(overlayData.Date).add(1, "month"),
  );
  const [, setRepeatUntilDateError] = useState(false);

  // Fetch holiday for the selected date
  useEffect(() => {
    const fetchHolidayForDate = async () => {
      if (!date) {
        setSelectedDateHoliday(null);
        return;
      }

      try {
        const dateKey = date.format("YYYY-MM-DD");
        const response = await api.get("/holidays", {
          params: {
            startDate: dateKey,
            endDate: dateKey,
            active: true,
          },
        });

        if (response?.data && response.data.length > 0) {
          setSelectedDateHoliday(response.data[0]);
        } else {
          setSelectedDateHoliday(null);
        }
      } catch (error) {
        console.error("Error fetching holiday:", error);
        setSelectedDateHoliday(null);
      }
    };

    fetchHolidayForDate();
  }, [date]);

  const [projectNumber, setProjectNumber] = useState(
    overlayData.ProjectNumber || "",
  );

  const [interpretationType, setInterpretationType] = useState(
    isNewAssignment ? "" : overlayData.InterpretationType,
  );

  const [client, setClient] = useState(
    isNewAssignment ? "" : overlayData.Client,
  );

  const [requestor, setRequestor] = useState(
    isNewAssignment ? "" : overlayData.Requestor,
  );

  const [invoiceTo, setInvoiceTo] = useState(
    isNewAssignment ? "" : overlayData.Invoicer,
  );

  const [language, setLanguage] = useState(
    isNewAssignment
      ? ""
      : overlayData.ServiceTargetLanguages?.[0]?.Language || "",
  );

  const [linguist, setLinguist] = useState(
    isNewAssignment ? "" : overlayData.Linguist,
  );

  const [address, setAddress] = useState(
    isNewAssignment ? "" : overlayData.Address,
  );

  const [interpretationStatus, setInterpretationStatus] = useState(
    isNewAssignment
      ? "Requested"
      : completing
        ? "Completed"
        : overlayData.InterpretationStatus,
  );

  const [cancellationReason, setCancellationReason] = useState(
    isNewAssignment ? "" : overlayData.CancellationReason,
  );

  const [accountCharge, setAccountCharge] = useState(
    isNewAssignment ? "" : overlayData.AccountCharge,
  );

  const [totalCost, setTotalCost] = useState(
    isNewAssignment ? "" : overlayData.TotalCost,
  );

  const [totalCostHelperText, setTotalCostHelperText] = useState("");
  const [accountChargeHelperText, setAccountChargeHelperText] = useState("");
  const [totalLinguistPayHelperText, setTotalLinguistPayHelperText] =
    useState("");

  const [linguistPay, setLinguistPay] = useState(
    isNewAssignment ? "" : overlayData.LinguistPay,
  );

  const [additionalCost, setAdditionalCost] = useState(
    isNewAssignment ? 0 : overlayData.AdditionalCost,
  );

  const [mode, setMode] = useState(
    overlayData.Mode === null ||
      overlayData.Mode === "" ||
      overlayData.Mode === undefined
      ? "Consecutive"
      : overlayData.Mode,
  );

  const getDurationValue = (timeStart, timeEnd) => {
    if (timeStart && timeEnd && timeStart.isValid() && timeEnd.isValid()) {
      const diffMs = timeEnd.diff(timeStart);

      // Convert to minutes and round up to nearest 15-minute increment
      const minutes = diffMs / 1000 / 60;
      const roundedMinutes = Math.ceil(minutes / 15) * 15;
      const roundedMs = roundedMinutes * 60 * 1000;

      return dayjs.utc(roundedMs);
    }
    return null;
  };

  const [rate, setRate] = useState(isNewAssignment ? 0 : overlayData.Rate);

  // Track original linguist and helper text for historical rates
  const [originalLinguistId] = useState(
    isNewAssignment ? null : overlayData.Linguist?.id,
  );
  const [rateHelperText, setRateHelperText] = useState("");

  // On load, if linguist rate changed since assignment, show both rates
  useEffect(() => {
    if (isNewAssignment) return;

    // Prefer latest linguist data from overlayData.Linguists (matched by id, then Name)
    const matchedLinguist =
      overlayData.Linguists?.find((ling) =>
        overlayData.Linguist?.id
          ? ling.id === overlayData.Linguist.id
          : overlayData.Linguist?.Name
            ? ling.Name === overlayData.Linguist.Name
            : false,
      ) || overlayData.Linguist;

    const linguistCurrentRateRaw =
      matchedLinguist?.LinguistLanguages?.[0]?.Rate ?? null;
    const assignmentRateRaw = overlayData.Rate;

    const linguistCurrentRateNum = Number(linguistCurrentRateRaw);
    const assignmentRateNum = Number(assignmentRateRaw);

    const linguistCurrentRate = Number.isNaN(linguistCurrentRateNum)
      ? 0
      : linguistCurrentRateNum;
    const assignmentRate = Number.isNaN(assignmentRateNum)
      ? 0
      : assignmentRateNum;

    if (assignmentRate !== linguistCurrentRate) {
      setRateHelperText(
        `Linguist New Rate: $${linguistCurrentRate} | Linguist Assignment Rate: $${assignmentRate}`,
      );
    }
  }, [
    isNewAssignment,
    overlayData.Linguist,
    overlayData.Linguists,
    overlayData.Rate,
  ]);

  const [contactPersonName, setContactPersonName] = useState(
    isNewAssignment ? "" : overlayData.ContactPersonName,
  );

  const [contactPersonNumber, setContactPersonNumber] = useState(
    isNewAssignment ? "" : overlayData.ContactPersonNumber,
  );

  const [twoHoursMin, setTwoHoursMin] = useState(
    isNewAssignment
      ? (linguist && linguist.MinimumHours && linguist.MinimumHours === 2) ||
          false
      : overlayData.ApplyTwoHourMinimum ||
          (linguist && linguist.MinimumHours && linguist.MinimumHours === 2) ||
          false,
  );

  const [profit, setProfit] = useState(
    isNewAssignment ? "" : overlayData.Profit,
  );

  const [margin, setMargin] = useState(
    isNewAssignment ? "" : overlayData.Margin,
  );

  const [note, setNote] = useState("");
  const [mentionTrigger, setMentionTrigger] = useState(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionCursor, setMentionCursor] = useState(null);
  const [isMentionOpen, setIsMentionOpen] = useState(false);
  const [isMentionLoading, setIsMentionLoading] = useState(false);
  const [serviceTagSuggestions, setServiceTagSuggestions] = useState([]);
  const [members, setMembers] = useState([]);

  const initialClientRate = isNewAssignment
    ? ""
    : overlayData.ServiceClientRate
      ? overlayData.ServiceClientRate
      : overlayData.ServiceTargetLanguages?.[0]?.LanguageID
        ? updateClientRate(
            overlayData.Client,
            overlayData.ServiceTargetLanguages[0].LanguageID,
          )
        : "";

  const [clientRate, setClientRate] = useState(
    isNewAssignment ? "" : initialClientRate,
  );

  const [clientRateSimultaneous, setClientRateSimultaneous] = useState(
    isNewAssignment
      ? initialClientRate * 1.5
      : overlayData.ServiceClientRateSimultaneous,
  );

  const [rateSimultaneous, setRateSimultaneous] = useState(
    isNewAssignment ? 0 : overlayData.RateSimultaneous,
  );

  const [additionalLinguistCost, setAdditionalLinguistCost] = useState(
    isNewAssignment ? "" : overlayData.AdditionalLinguistCost,
  );

  const [equipmentCost, setEquipmentCost] = useState(
    isNewAssignment ? "" : overlayData.EquipmentCost,
  );

  const [totalLinguistPay, setTotalLinguistPay] = useState(
    isNewAssignment ? "" : overlayData.TotalLinguistPay,
  );

  const [clientRateDiscount, setClientRateDiscount] = useState(
    isNewAssignment ? 0 : overlayData.ClientRateDiscount,
  );

  const [timeStart, setTimeStart] = useState(
    isNewAssignment
      ? overlayData.PrefilledData
        ? overlayData.PrefilledData.TimeStart
        : ""
      : dayjs(overlayData.TimeStart, "HH:mm:ss"),
  );
  const [timeEnd, setTimeEnd] = useState(
    isNewAssignment
      ? overlayData.PrefilledData
        ? overlayData.PrefilledData.TimeEnd
        : ""
      : dayjs(overlayData.TimeEnd, "HH:mm:ss"),
  );

  const [duration, setDuration] = useState(
    isNewAssignment
      ? overlayData.PrefilledData
        ? getDurationValue(
            overlayData.PrefilledData.TimeStart,
            overlayData.PrefilledData.TimeEnd,
          )
        : null
      : dayjs(overlayData.Duration, "HH:mm:ss"),
  );

  // Initialize actual times as null
  const [actualStart, setActualStart] = useState(
    isNewAssignment
      ? ""
      : overlayData.ActualStart === "00:00:00"
        ? completing
          ? dayjs(overlayData.TimeStart, "HH:mm:ss")
          : null
        : dayjs(overlayData.ActualStart, "HH:mm:ss"),
  );
  const [actualEnd, setActualEnd] = useState(
    isNewAssignment
      ? ""
      : overlayData.ActualEnd === "00:00:00"
        ? completing
          ? dayjs(overlayData.TimeEnd, "HH:mm:ss")
          : null
        : dayjs(overlayData.ActualEnd, "HH:mm:ss"),
  );
  const [actualDuration, setActualDuration] = useState(
    isNewAssignment
      ? null
      : overlayData.ActualDuration === "00:00:00"
        ? completing
          ? dayjs(overlayData.Duration, "HH:mm:ss")
          : null
        : dayjs(overlayData.ActualDuration, "HH:mm:ss"),
  );

  // Also initialize scheduling and preparation times as null
  const [schedulingTime, setSchedulingTime] = useState(
    isNewAssignment
      ? null
      : overlayData.SchedulingTime === "00:00:00"
        ? null
        : dayjs(overlayData.SchedulingTime, "HH:mm:ss"),
  );
  const [preparationTime, setPreparationTime] = useState(
    isNewAssignment
      ? null
      : overlayData.PreparationTime === "00:00:00"
        ? null
        : dayjs(overlayData.PreparationTime, "HH:mm:ss"),
  );
  const [totalDuration, setTotalDuration] = useState(
    isNewAssignment
      ? overlayData.PrefilledData
        ? getDurationValue(
            overlayData.PrefilledData.TimeStart,
            overlayData.PrefilledData.TimeEnd,
          )
        : null
      : overlayData.TotalDuration === "00:00:00"
        ? completing
          ? dayjs(overlayData.Duration, "HH:mm:ss")
          : null
        : dayjs(overlayData.TotalDuration, "HH:mm:ss"),
  );

  const [mileageRoundtrip, setMileageRoundtrip] = useState(
    isNewAssignment ? 0 : overlayData.MileageRoundtrip,
  );

  const [parkingFeesTolls, setParkingFeesTolls] = useState(
    isNewAssignment ? 0 : overlayData.ParkingFeesTolls,
  );

  const [mileageRates, setMileageRates] = useState([]);

  const getMileageRateForYear = useCallback(
    (year) => {
      if (!year || mileageRates.length === 0) return 0.7; // Fallback to 0.7
      const rate = mileageRates.find((r) => r.year === year);
      return rate ? parseFloat(rate.rate) : 0.7; // Fallback to 0.7 if year not found
    },
    [mileageRates],
  );

  const [mileageCost, setMileageCost] = useState(
    isNewAssignment ? 0 : overlayData.MileageCost,
  );

  const [details, setDetails] = useState(
    isNewAssignment ? "" : overlayData.Details,
  );

  const [showAllLinguists, setShowAllLinguists] = useState(false);

  const [showRecurring, setShowRecurring] = useState(false);

  const [filterLingsByLangs, setFilterLingsByLangs] = useState(true);

  const APP_TIMEZONE = "America/New_York";

  // Date counts cache for project number generation
  const [dateCountsCache, setDateCountsCache] = useState({});
  const [, setDateCountsLoading] = useState(false);

  const [showActualTimes, setShowActualTimes] = useState(
    completing
      ? true
      : isNewAssignment
        ? false
        : overlayData.ActualDuration === "00:00:00"
          ? false
          : true,
  );

  const [allowDetailsEditing, setAllowDetailsEditing] = useState(false);
  const [allowTotalDurationEditing, setAllowTotalDurationEditing] =
    useState(false);
  const [
    disableAutomaticCalculationOfCharges,
    setDisableAutomaticCalculationOfCharges,
  ] = useState(
    isNewAssignment ? false : overlayData.DisableAutoChargeCalc || false,
  );

  const [allowShortAssignments, setAllowShortAssignments] = useState(
    isNewAssignment ? false : overlayData.IsShortAssignment || false,
  );

  const [nameError, setNameError] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [projectNumberError, setProjectNumberError] = useState(false);
  const [projectNumberExists, setProjectNumberExists] = useState(false);
  const [interpretationTypeError, setInterpretationTypeError] = useState(false);
  const [clientError, setClientError] = useState(false);
  const [requestorError, setRequestorError] = useState(false);
  const [invoiceToError, setInvoiceToError] = useState(false);
  const [languageError, setLanguageError] = useState(false);
  const [linguistError, setLinguistError] = useState(false);
  const [addressError, setAddressError] = useState(false);

  const [repeatEveryError, setRepeatEveryError] = useState(false);
  const [periodError, setPeriodError] = useState(false);
  const [repeatEvery, setRepeatEvery] = useState(1);

  const [period, setPeriod] = useState("Week");
  const periodsList = ["Day", "Week", "Month"];
  const repeatEveryList = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
  const daysList = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const [tempSelectedDays, setTempSelectedDays] = useState([]);

  const [recursionStatusText, setRecursionStatusText] = useState("");

  const [recurrenceDates, setRecurrenceDates] = useState([]);

  // Fetch bulk date counts on component mount (60 days back, 180 days forward)
  useEffect(() => {
    const fetchDateCounts = async () => {
      setDateCountsLoading(true);
      try {
        const today = dayjs().tz(APP_TIMEZONE);
        const startDate = today.subtract(60, "day").format("YYYY-MM-DD");
        const endDate = today.add(180, "day").format("YYYY-MM-DD");

        const response = await api.get(
          "/services/interpretations/date-counts",
          {
            params: { startDate, endDate },
          },
        );

        // Convert array to object for fast lookup
        const cache = {};
        response.data.forEach((item) => {
          cache[item.date] = parseInt(item.count, 10);
        });

        setDateCountsCache(cache);
      } catch (error) {
        console.error("Error fetching date counts:", error);
      } finally {
        setDateCountsLoading(false);
      }
    };

    fetchDateCounts();
  }, []);

  // Get count for a specific date (with on-demand fallback)
  const getDateCount = async (dateValue) => {
    if (!dateValue) {
      return 0;
    }

    const dateStr = dayjs(dateValue)
      .tz(APP_TIMEZONE, true)
      .format("YYYY-MM-DD");

    // Check cache first
    if (dateCountsCache.hasOwnProperty(dateStr)) {
      return dateCountsCache[dateStr];
    }

    // Date not in cache, fetch on-demand
    try {
      const response = await api.get("/services/interpretations/date-count", {
        params: { date: dateStr },
      });

      const count = parseInt(response.data.count, 10);

      // Add to cache for future use
      setDateCountsCache((prev) => ({
        ...prev,
        [dateStr]: count,
      }));

      return count;
    } catch (error) {
      console.error("[getDateCount] Error fetching date count:", error);
      return 0;
    }
  };

  // Generate project number from date and count
  const generateProjectNumber = (dateValue, count) => {
    if (!dateValue) return "";

    const baseDate = dayjs(dateValue).tz(APP_TIMEZONE, true);
    const index = count + 1;
    const paddedIndex = index < 10 ? `0${index}` : `${index}`;
    return baseDate.format("YYMMDD") + paddedIndex;
  };

  const handleDaysChange = (days) => {
    setTempSelectedDays(days);

    if (days.length === 7 && period === "Week") {
      setPeriod("Day");
      handleRecursionChange(
        date,
        repeatEvery,
        "Day",
        repeatUntilDate,
        days,
        showRecurring,
      );
    } else if (days.length < 7 && period === "Day") {
      setPeriod("Week");
      handleRecursionChange(
        date,
        repeatEvery,
        "Week",
        repeatUntilDate,
        days,
        showRecurring,
      );
    } else
      handleRecursionChange(
        date,
        repeatEvery,
        period,
        repeatUntilDate,
        days,
        showRecurring,
      );
  };

  const handleRecursionChange = async (
    date,
    repeatEvery,
    period,
    repeatUntil,
    days,
    recurring,
  ) => {
    let finalRecursionText;

    if (!recurring) return;

    if (
      date === null ||
      date === "" ||
      !repeatUntil ||
      !repeatUntil.isValid?.()
    ) {
      setRecursionStatusText("Please set a date first");
      return;
    } else if (period === "Month") {
      const repeatEveryText =
        repeatEvery === 1
          ? " of Each Month until "
          : repeatEvery === 2
            ? " every Other Month "
            : " every " + repeatEvery + " Months ";

      finalRecursionText =
        "Assignment will be repeated on day " +
        date.format("DD") +
        repeatEveryText +
        repeatUntil.format("dddd, MMMM DD, YYYY");
    } else if (period === "Day") {
      const repeatEveryText =
        repeatEvery === 1 ? "" : repeatEvery === 2 ? "Other " : repeatEvery;

      const periodText =
        repeatEvery === 1 || repeatEvery === 2 ? period : period + "s";

      finalRecursionText =
        "Assignment will be repeated every " +
        repeatEveryText +
        " " +
        periodText +
        " until " +
        repeatUntil.format("dddd, MMMM DD, YYYY");
    } else {
      const daysText = formatDays(days);

      const repeatEveryText =
        repeatEvery === 1
          ? ""
          : repeatEvery === 2
            ? "Other "
            : repeatEvery + " ";

      const periodText =
        repeatEvery === 1 || repeatEvery === 2 ? period : period + "s";

      finalRecursionText =
        "Assignment will be repeated every " +
        repeatEveryText +
        "" +
        periodText +
        " on " +
        daysText +
        " until " +
        repeatUntil.format("dddd, MMMM DD, YYYY");
    }

    const tempRecurrenceDates = await getRecurrenceDates(
      date,
      repeatEvery,
      period,
      repeatUntil,
      days,
    );

    setRecurrenceDates(tempRecurrenceDates);

    const totalAssignment =
      tempRecurrenceDates === null
        ? ""
        : ".\nThere will be " + tempRecurrenceDates.length + " in total.";

    setRecursionStatusText(finalRecursionText + totalAssignment);
  };

  const dayIndex = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };

  function formatDays(days) {
    if (!days?.length) return "";

    const full = days.map((d) => dayjs().day(dayIndex[d]).format("dddd"));
    if (full.length === 1) return full[0];
    if (full.length === 2) return `${full[0]} and ${full[1]}`;
    return `${full.slice(0, -1).join(", ")}, and ${full[full.length - 1]}`;
  }

  const normalizeWeekday = (d) => {
    if (typeof d === "number" && d >= 0 && d <= 6) return d;

    if (typeof d === "string") {
      const key = d.trim().toLowerCase();
      const map = {
        sun: 0,
        sunday: 0,
        mon: 1,
        monday: 1,
        tue: 2,
        tues: 2,
        tuesday: 2,
        wed: 3,
        weds: 3,
        wednesday: 3,
        thu: 4,
        thur: 4,
        thurs: 4,
        thursday: 4,
        fri: 5,
        friday: 5,
        sat: 6,
        saturday: 6,
      };
      if (key in map) return map[key];
    }
    return null;
  };

  const getRecurrenceDates = async (
    date,
    repeatEvery = 1,
    period = "Day",
    repeatUntil,
    days = [],
  ) => {
    const start = dayjs(date);
    const end = dayjs(repeatUntil);

    if (
      !start.isValid() ||
      !end.isValid() ||
      repeatEvery < 1 ||
      start.isAfter(end)
    ) {
      return [];
    }

    // Preserve the time of day from the original "date"
    const baseTime = {
      hour: start.hour(),
      minute: start.minute(),
      second: start.second(),
      millisecond: start.millisecond(),
    };

    const withBaseTime = (d) =>
      d
        .hour(baseTime.hour)
        .minute(baseTime.minute)
        .second(baseTime.second)
        .millisecond(baseTime.millisecond);

    const results = [];
    const seen = new Set(); // avoid duplicates when multiple weekdays hit the same date
    const datesToFetch = []; // Collect all dates we need counts for

    if (period === "Day") {
      for (
        let cur = start;
        !cur.isAfter(end);
        cur = cur.add(repeatEvery, "day")
      ) {
        const instance = withBaseTime(cur);
        const key = instance.valueOf();
        if (!seen.has(key)) {
          seen.add(key);
          datesToFetch.push(instance);
        }
      }
    } else if (period === "Week") {
      // Normalize selected weekdays; default to the start date's weekday if none provided
      let selected = Array.isArray(days) ? days : [];
      selected = selected
        .map(normalizeWeekday)
        .filter((n) => Number.isInteger(n) && n >= 0 && n <= 6);

      if (selected.length === 0) {
        selected = [start.day()];
      }

      // Sort and unique
      selected = Array.from(new Set(selected)).sort((a, b) => a - b);

      for (const wd of selected) {
        // Find the first occurrence on/after start that falls on this weekday
        const delta = (wd - start.day() + 7) % 7; // 0..6 days forward
        let first = start.add(delta, "day");

        // Walk forward in blocks of repeatEvery weeks for this weekday
        for (
          let cur = first;
          !cur.isAfter(end);
          cur = cur.add(repeatEvery * 7, "day")
        ) {
          const instance = withBaseTime(cur);
          const key = instance.valueOf();
          if (!seen.has(key)) {
            seen.add(key);
            datesToFetch.push(instance);
          }
        }
      }
    } else if (period === "Month") {
      // Repeat on the same day-of-month/time as the start date
      for (
        let i = 0, cur = start;
        !cur.isAfter(end);
        i += 1, cur = start.add(i * repeatEvery, "month")
      ) {
        const instance = withBaseTime(cur);
        const key = instance.valueOf();
        if (!seen.has(key)) {
          seen.add(key);
          datesToFetch.push(instance);
        }
      }
    }

    // Fetch counts for all dates in parallel
    const counts = await Promise.all(
      datesToFetch.map(async (instance) => {
        const count = await getDateCount(instance);
        return { instance, count };
      }),
    );

    // Generate project numbers with the fetched counts
    counts.forEach(({ instance, count }) => {
      const projectNum = generateProjectNumber(instance, count);
      results.push({ time: instance, projectNumber: projectNum });
    });

    // Ensure chronological order
    results.sort((a, b) => a.time.valueOf() - b.time.valueOf());
    return results;
  };

  const [interpretationStatusError, setInterpretationStatusError] =
    useState(false);
  const [accountChargeError, setAccountChargeError] = useState(false);
  const [totalCostError, setTotalCostError] = useState(false);
  const [linguistPayError, setLinguistPayError] = useState(false);
  const [additionalCostError, setAdditionalCostError] = useState(false);
  const [modeError, setModeError] = useState(false);
  const [rateError, setRateError] = useState(false);
  const [clientRateError, setClientRateError] = useState(false);
  const [clientRateSimultaneousError, setClientRateSimultaneousError] =
    useState(false);
  const [rateSimultaneousError, setRateSimultaneousError] = useState(false);
  const [additionalLinguistCostError, setAdditionalLinguistCostError] =
    useState(false);
  const [equipmentCostError, setEquipmentCostError] = useState(false);
  const [clientRateDiscountError, setClientRateDiscountError] = useState(false);
  const [timeStartError, setTimeStartError] = useState(false);
  const [timeEndError, setTimeEndError] = useState(false);
  useState(false);
  const [actualStartError, setActualStartError] = useState(false);
  const [actualEndError, setActualEndError] = useState(false);
  const [schedulingTimeError, setSchedulingTimeError] = useState(false);
  const [mileageRoundtripError, setMileageRoundtripError] = useState(false);
  const [parkingFeesTollsError, setParkingFeesTollsError] = useState(false);
  const [mileageCostError, setMileageCostError] = useState(false);
  const [detailsError, setDetailsError] = useState(false);
  const [shouldValidate, setShouldValidate] = useState(false);

  const [progress, setProgress] = useState("");
  const [progressMessage, setProgressMessage] = useState("");
  const [shakingFields, setShakingFields] = useState(new Set());

  const [, setHasChanges] = useState(false);

  const initialDataRef = useRef(null);
  const updateCurrentProgressRef = useRef(() => {});
  const addBulkInterpretationsRef = useRef(() => {});
  const addNewInterpretationRef = useRef(() => {});
  const updateInterpretationRef = useRef(() => {});
  const saveInterpretationRef = useRef(() => {});
  const attemptSaveRef = useRef(() => {});
  const noteInputRef = useRef(null);

  const handleNoteTagState = useCallback(
    (value, cursorPosition) => {
      if (!isEditable) {
        setIsMentionOpen(false);
        return;
      }
      const context = getTagContextAtCursor(value, cursorPosition);
      if (!context) {
        setIsMentionOpen(false);
        setMentionTrigger(null);
        setMentionQuery("");
        setMentionCursor(null);
        return;
      }
      setMentionTrigger(context.trigger);
      setMentionQuery(context.query);
      setMentionCursor(context.cursorPosition);
      setIsMentionOpen(true);
    },
    [isEditable]
  );

  useEffect(() => {
    let isMounted = true;
    const fetchMembers = async () => {
      try {
        const response = await api.get("/members");
        if (!isMounted) return;
        const allMembers = Array.isArray(response.data) ? response.data : [];
        setMembers(allMembers.filter((member) => !!getMemberMentionToken(member)));
      } catch (error) {
        console.error("Error fetching mention members:", error);
        if (isMounted) setMembers([]);
      }
    };
    fetchMembers();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (!isMentionOpen || mentionTrigger !== "#" || !mentionQuery) {
      setServiceTagSuggestions([]);
      setIsMentionLoading(false);
      return;
    }
    const timeoutId = setTimeout(async () => {
      setIsMentionLoading(true);
      try {
        const response = await api.get(
          `/services/tag-search?query=${encodeURIComponent(mentionQuery)}&limit=10`
        );
        setServiceTagSuggestions(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching service tag suggestions:", error);
        setServiceTagSuggestions([]);
      } finally {
        setIsMentionLoading(false);
      }
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [isMentionOpen, mentionQuery, mentionTrigger]);

  const userTagSuggestions = useMemo(() => {
    if (mentionTrigger !== "@") return [];
    const query = String(mentionQuery || "").toLowerCase();
    return members
      .filter((member) => {
        if (!query) return true;
        const name = String(member?.Name || "").toLowerCase();
        const email = String(member?.Email || "").toLowerCase();
        const token = getMemberMentionToken(member).toLowerCase();
        return name.includes(query) || email.includes(query) || token.includes(query);
      })
      .slice(0, 10);
  }, [members, mentionQuery, mentionTrigger]);

  const handleSelectServiceTag = useCallback(
    (service) => {
      const projectNumber = service?.ProjectNumber;
      if (projectNumber === null || projectNumber === undefined) return;
      const cursorPosition = typeof mentionCursor === "number" ? mentionCursor : note.length;
      const beforeCursor = note.slice(0, cursorPosition);
      const afterCursor = note.slice(cursorPosition);
      const replacedBefore = beforeCursor.replace(
        /(^|\s)#([a-zA-Z0-9._-]*)$/,
        (fullMatch, leadingWhitespace) => `${leadingWhitespace}#${projectNumber}`
      );
      const nextValue = `${replacedBefore} ${afterCursor.replace(/^\s*/, "")}`;
      const nextCursor = replacedBefore.length + 1;
      setNote(nextValue);
      setIsMentionOpen(false);
      setServiceTagSuggestions([]);
      setMentionQuery("");
      setMentionCursor(null);
      setMentionTrigger(null);
      requestAnimationFrame(() => {
        const input = noteInputRef.current;
        if (input && typeof input.focus === "function") {
          input.focus();
          if (typeof input.setSelectionRange === "function") {
            input.setSelectionRange(nextCursor, nextCursor);
          }
        }
      });
    },
    [mentionCursor, note]
  );

  const handleSelectUserTag = useCallback(
    (member) => {
      const mentionToken = getMemberMentionToken(member);
      if (!mentionToken) return;
      const cursorPosition = typeof mentionCursor === "number" ? mentionCursor : note.length;
      const beforeCursor = note.slice(0, cursorPosition);
      const afterCursor = note.slice(cursorPosition);
      const replacedBefore = beforeCursor.replace(
        /(^|\s)@([a-zA-Z0-9._-]*)$/,
        (fullMatch, leadingWhitespace) => `${leadingWhitespace}@${mentionToken}`
      );
      const nextValue = `${replacedBefore} ${afterCursor.replace(/^\s*/, "")}`;
      const nextCursor = replacedBefore.length + 1;
      setNote(nextValue);
      setIsMentionOpen(false);
      setMentionQuery("");
      setMentionCursor(null);
      setMentionTrigger(null);
      requestAnimationFrame(() => {
        const input = noteInputRef.current;
        if (input && typeof input.focus === "function") {
          input.focus();
          if (typeof input.setSelectionRange === "function") {
            input.setSelectionRange(nextCursor, nextCursor);
          }
        }
      });
    },
    [mentionCursor, note]
  );

  /**
   * Helper function to get shake animation styles
   * Apply to TextFields/Autocomplete that should shake when validation fails
   */
  const getShakeSx = (fieldId) => {
    const isShaking = shakingFields.has(fieldId);
    return isShaking
      ? {
          animation: "shake 0.6s",
          "@keyframes shake": {
            "0%, 100%": { transform: "translateX(0)" },
            "10%, 30%, 50%, 70%, 90%": { transform: "translateX(-5px)" },
            "20%, 40%, 60%, 80%": { transform: "translateX(5px)" },
          },
        }
      : {};
  };

  const getBorderColorTimes = (newData, oldData, newData2) => {
    if (!isNewAssignment && newData !== oldData) {
      return theme.palette.info[500];
    } else {
      return newData === null || newData === ""
        ? newData2 === null && newData2 === ""
          ? theme.palette.primary.main
          : theme.palette.error.main
        : theme.palette.primary.main;
    }
  };

  /**
   * VALIDATION CONFIGURATION
   * Easily manage required fields and their validation behavior
   *
   * Structure: { fieldId, value, message, isRequired }
   * - isRequired: true = must be filled before save, false = shows in progress message
   */
  const getValidationFields = () => [
    // REQUIRED FIELDS (Must be filled)
    {
      fieldId: "Date",
      value: date,
      message: "Please add a Date",
      isRequired: true,
    },
    {
      fieldId: "ProjectNumber",
      value: projectNumber,
      message: "Please add a Project Number",
      isRequired: true,
    },
    {
      fieldId: "Name",
      value: name,
      message: "Please add a Name",
      isRequired: true,
    },
    {
      fieldId: "InterpretationType",
      value: interpretationType,
      message: "Please add an Interpretation Type",
      isRequired: true,
    },
    {
      fieldId: "Language",
      value: language,
      message: "Please add a Language",
      isRequired: true,
    },
    {
      fieldId: "Client",
      value: client,
      message: "Please add a Client",
      isRequired: true,
    },
    {
      fieldId: "ClientRate",
      value: clientRate,
      message: "Please add a Client Rate",
      isRequired: true,
    },
    {
      fieldId: "Requestor",
      value: requestor,
      message: "Please add a Requestor",
      isRequired: true,
    },
    {
      fieldId: "Linguist",
      value: linguist,
      message: "Please add a Linguist",
      isRequired: true,
    },

    // HIGHLY RECOMMENDED FIELDS (Show Progress)
    {
      fieldId: "Rate",
      value: rate,
      message: "Please add a Rate",
      isRequired: false,
    },
    {
      fieldId: "StartTime",
      value: timeStart,
      message: "Please add a Start Time",
      isRequired: false,
    },
    {
      fieldId: "EndTime",
      value: timeEnd,
      message: "Please add an End Time",
      isRequired: false,
    },
    {
      fieldId: "InterpretationStatus",
      value: interpretationStatus,
      message: "Please add an Interpretation Status",
      isRequired: false,
    },
    {
      fieldId: "LinguistPay",
      value: linguistPay,
      message: "Please add a Linguist Pay",
      isRequired: false,
    },
    {
      fieldId: "TotalCost",
      value: totalCost,
      message: "Please add a Total Cost",
      isRequired: false,
    },
    {
      fieldId: "AccountCharge",
      value: accountCharge,
      message: "Please add an Account Charge",
      isRequired: false,
    },
    {
      fieldId: "Details",
      value: details,
      message: "Please add Details",
      isRequired: false,
    },
  ];

  /**
   * Trigger shake animation on missing required fields
   */
  const triggerMissingFieldsShake = () => {
    const validationFields = getValidationFields();
    const missingRequiredFields = validationFields.filter(
      (field) =>
        field.isRequired && (field.value === null || field.value === ""),
    );

    if (missingRequiredFields.length > 0) {
      const newShakingFields = new Set(
        missingRequiredFields.map((field) => field.fieldId),
      );
      setShakingFields(newShakingFields);

      // Remove shake effect after animation completes (600ms)
      setTimeout(() => {
        setShakingFields(new Set());
      }, 600);

      return false; // Validation failed
    }

    return true; // Validation passed
  };

  const updateCurrentProgress = () => {
    const validationFields = getValidationFields();
    const requiredFields = validationFields.filter((f) => f.isRequired);
    const recommendedFields = validationFields.filter((f) => !f.isRequired);

    // Check for missing required fields
    const missingRequiredField = requiredFields.find(
      (field) => field.value === null || field.value === "",
    );

    // Check for missing recommended fields
    const missingRecommendedField = recommendedFields.find(
      (field) => field.value === null || field.value === "",
    );

    // Determine progress
    const totalFields = validationFields.length;
    const filledFields = validationFields.filter(
      (field) => field.value !== null && field.value !== "",
    ).length;
    const percentProgress = (filledFields * 100) / totalFields;

    // Set message
    let message = "All Done!";
    if (missingRequiredField) {
      message = missingRequiredField.message;
    } else if (missingRecommendedField) {
      message = missingRecommendedField.message;
    }

    setProgressMessage(Number(percentProgress).toFixed(2) + "%: " + message);
    setProgress(percentProgress);
  };
  updateCurrentProgressRef.current = updateCurrentProgress;

  const handleClear = () => {
    const defaultLinguist = overlayData.Linguists?.find(
      (ling) => ling.id === 1,
    );

    setLinguist(defaultLinguist || { Name: "N/A", id: 1 });

    setRateHelperText("");

    setRate(0);
    setRateSimultaneous(0);

    setTwoHoursMin(false);
  };

  const filterLinguistsByLanguage = (linguists, lang, showAllLinguists) => {
    if (!filterLingsByLangs) return linguists;

    if (!lang || lang === "") return linguists;

    return linguists.filter((linguist) => {
      if (linguist.Name === "N/A") return true;

      const languageMatches =
        linguist.LinguistLanguages &&
        linguist.LinguistLanguages.some(
          (lg) => lg.Language && lg.Language.Name === lang,
        );
      const statusMatches = showAllLinguists || linguist.Status === "Current";
      return languageMatches && statusMatches;
    });
  };

  const fillDetails = (requestor, name, language, type) => {
    // Ensure values are truthy; if empty string or null, treat them as missing.
    const req = requestor ? requestor.trim() : "";
    const nm = name ? name.trim() : "";
    const lang = language ? language.trim() : "";

    const hasRequestor = req.length > 0;
    const hasName = nm.length > 0;
    const hasLanguage = lang.length > 0;

    if (hasRequestor && hasName && hasLanguage) {
      setDetails(`${req} - ${nm} - ${lang} ${type}`);
    } else if (hasRequestor && !hasName && hasLanguage) {
      setDetails(`${req} - ${lang} ${type}`);
    } else if (!hasRequestor && hasName && hasLanguage) {
      setDetails(`${nm} - ${lang} ${type}`);
    } else if (hasRequestor && hasName && !hasLanguage) {
      setDetails(`${req} - ${nm} - ${type}`);
    } else if (hasRequestor && !hasName && !hasLanguage) {
      setDetails(`${req} - ${type}`);
    } else if (!hasRequestor && hasName && !hasLanguage) {
      setDetails(`${nm} - ${type}`);
    } else if (!hasRequestor && !hasName && hasLanguage) {
      setDetails(`${lang} ${type}`);
    } else {
      setDetails("");
    }
  };

  dayjs.extend(durationPlugin);

  function cancelCharges() {
    setAccountChargeHelperText("");

    setTotalCostHelperText("");

    setTotalLinguistPayHelperText("");

    setLinguistPay(0);

    setTotalLinguistPay(0);

    setTotalCost(0);
    setAccountCharge(0);

    setProfit(0);
    setMargin(0);
  }

  const sumDurationValues = (durations) => {
    const totalMinutes = durations.reduce((acc, d) => {
      if (!d) return acc;

      // Handle dayjs objects
      if (d.format) {
        const timeString = d.format("HH:mm");
        const [hours, minutes] = timeString.split(":").map(Number);
        return acc + hours * 60 + minutes;
      }

      // Handle numeric values (already in minutes)
      return acc + d;
    }, 0);

    return dayjs().startOf("day").add(totalMinutes, "minute");
  };

  const validateFields = () => {
    setNameError(name === null);
    setDateError(date === null);
    setProjectNumberError(projectNumber === null);
    setInterpretationTypeError(interpretationType === null);
    setClientError(client === null);
    setClientRateError(clientRate === null);
    setRequestorError(requestor === null);
    setLanguageError(language === null);
    setLinguistError(linguist === null);
    setRateError(rate === null);
    setAddressError(address === null);
    setInterpretationStatusError(interpretationStatus === null);
    setTimeStartError(timeStart === null);
    setTimeEndError(timeEnd === null);
    setAccountChargeError(accountCharge === null);
    setTotalCostError(totalCost === null);
    setLinguistPayError(linguistPay === null);
    setDetailsError(details === null);
  };

  // Check if assignment was paid and if critical fields have changed
  const checkPaidWarning = () => {
    // Only check for existing assignments
    if (isNewAssignment) return false;

    // Check if the assignment was paid
    const wasPaid = overlayData.Paid === true || overlayData.Paid === 1;

    if (!wasPaid) return false;

    // Check if critical fields have changed
    const criticalChanges = [];

    // Check if client changed
    if (client?.id !== overlayData.Client?.id) {
      criticalChanges.push("Client");
    }

    // Check if linguist changed
    if (linguist?.id !== overlayData.Linguist?.id) {
      criticalChanges.push("Linguist");
    }

    // Check if account charge changed
    if (Number(accountCharge) !== Number(overlayData.AccountCharge)) {
      criticalChanges.push("Account Charge");
    }

    // Check if total cost changed
    if (Number(totalCost) !== Number(overlayData.TotalCost)) {
      criticalChanges.push("Total Cost");
    }

    // Check if linguist pay changed
    if (Number(linguistPay) !== Number(overlayData.LinguistPay)) {
      criticalChanges.push("Linguist Pay");
    }

    if (criticalChanges.length > 0) {
      let message = "⚠️ WARNING: This assignment has been marked as PAID.\n\n";
      message += `You are changing: ${criticalChanges.join(", ")}\n\n`;

      // Special check for linguist change - check if the linguist was paid
      if (criticalChanges.includes("Linguist")) {
        message +=
          "🔹 You are changing the linguist for this paid assignment.\n";
        message += `   Previous linguist: ${overlayData.Linguist?.Name || "None"}\n`;
        message += `   New linguist: ${linguist?.Name || "None"}\n\n`;
      }

      message += "Are you sure you want to proceed with these changes?";

      setPaidWarningMessage(message);
      return true;
    }

    return false;
  };

  function addBulkInterpretations() {
    setIsSavingChanges(true);
    setIsEditable(false);

    recurrenceDates.forEach((item, index) => {
      //('Adding Assignment '+(index+1)+ " / "+recurrenceDates.length+". Project Number: "+item['projectNumber'])
      //Maybe add a slight delay and a loading and a text to show progress later
      addNewInterpretationBulk(
        item["time"],
        item["projectNumber"],
        recurrenceDates.length,
        index === recurrenceDates.length - 1,
      );
    });
  }

  function addNewInterpretationBulk(
    singleDate,
    singleProjectNumber,
    length,
    isFinal,
  ) {
    const data = {
      Assignment: {
        id: 0,
        Date:
          singleDate && singleDate.isValid()
            ? singleDate.format("YYYY-MM-DD")
            : null,
        ProjectNumber: singleProjectNumber,
        CheckingDate: null,
        TimeStart: (timeStart && timeStart.format("HH:mm:ss")) || "00:00:00",
        TimeEnd: (timeEnd && timeEnd.format("HH:mm:ss")) || "00:00:00",
        ServiceCategory: "Interpretation",
        TotalDuration:
          (totalDuration && totalDuration.format("HH:mm:ss")) || "00:00:00",
        Duration: (duration && duration.format("HH:mm:ss")) || "00:00:00",
        ActualDuration:
          (actualDuration && actualDuration.format("HH:mm:ss")) || "00:00:00",
        Name: name,
        Details: details,
        InterpretationStatus:
          interpretationStatus === "" ? null : interpretationStatus,
        AccountCharge: accountCharge === "" ? null : accountCharge,
        TotalCost: totalCost === "" ? null : totalCost,
        AdditionalCost: additionalCost === "" ? null : additionalCost,
        LinguistPay: linguistPay === "" ? null : linguistPay,
        Profit: profit === "" ? null : profit,
        Margin: margin === "" ? null : margin,
        InterpretationType: interpretationType,
        Mode: mode === "" ? null : mode,
        Address: address,
        MileageRoundtrip: mileageRoundtrip === "" ? null : mileageRoundtrip,
        MileageCost: mileageCost === "" ? null : mileageCost,
        ParkingFeesTolls: parkingFeesTolls === "" ? null : parkingFeesTolls,
        PreparationTime:
          (preparationTime && preparationTime.format("HH:mm:ss")) || "00:00:00",
        SchedulingTime:
          (schedulingTime && schedulingTime.format("HH:mm:ss")) || "00:00:00",
        ActualStart:
          (actualStart && actualStart.format("HH:mm:ss")) || "00:00:00",
        ActualEnd: (actualEnd && actualEnd.format("HH:mm:ss")) || "00:00:00",
        Paid: false,
        Rate: rate === "" ? null : rate,
        RateSimultaneous: rateSimultaneous === "" ? null : rateSimultaneous,
        ServiceClientRate:
          clientRate === "" ? null : Number(clientRate).toFixed(2),
        ServiceClientRateSimultaneous:
          clientRateSimultaneous === ""
            ? null
            : Number(clientRateSimultaneous).toFixed(2),
        TotalLinguistPay: totalLinguistPay === "" ? null : totalLinguistPay,
        EquipmentCost: equipmentCost === "" ? null : equipmentCost,
        AdditionalLinguistCost:
          additionalLinguistCost === "" ? null : additionalLinguistCost,
        DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
        ApplyTwoHourMinimum: twoHoursMin,
        IsShortAssignment: allowShortAssignments,
        Deleted: false,
        Cancelled:
          interpretationStatus !== null
            ? interpretationStatus.startsWith("Cancel")
            : false,
        Invoiced: false,
        CancellationReason:
          cancellationReason === "" ? null : cancellationReason,
        CreatedBy: currentUser.id,
        CheckedBy: null,
        ContactPersonName: contactPersonName,
        ContactPersonNumber: contactPersonNumber,
      },
      Language: language,
      Client: client,
      Requestor: requestor,
      Linguist: linguist,
      Invoicer: invoiceTo,
    };

    api
      .post("/services/interpretations/addNewInterpretation", {
        data: data,
      })
      .then((response) => {
        if (note) addNewNote(response.data.id);

        const dataHistory = {
          Status: "Created",
          ServiceID: response.data.id,
          ProjectNumber: response.data.ProjectNumber,
          Date_FROM: null,
          Date_TO: response.data.Date,
          ClientID_FROM: null,
          ClientID_TO: response.data.ClientID,
          RequestorID_FROM: null,
          RequestorID_TO: response.data.RequestorID,
          LinguistID_FROM: null,
          LinguistID_TO: response.data.LinguistID,
          ChangedBy: currentUser.id,
          TimeStart_FROM: null,
          TimeStart_TO: response.data.TimeStart,
          TimeEnd_FROM: null,
          TimeEnd_TO: response.data.TimeEnd,
          TotalDuration_FROM: null,
          TotalDuration_TO: response.data.TotalDuration,
          Duration_FROM: null,
          Duration_TO: response.data.Duration,
          ActualDuration_FROM: null,
          ActualDuration_TO: response.data.ActualDuration,
          Name_FROM: null,
          Name_TO: response.data.Name,
          Details_FROM: null,
          Details_TO: response.data.Details,
          InterpretationStatus_FROM: null,
          InterpretationStatus_TO: response.data.InterpretationStatus,
          AccountCharge_FROM: null,
          AccountCharge_TO: response.data.AccountCharge,
          TotalCost_FROM: null,
          TotalCost_TO: response.data.TotalCost,
          AdditionalCost_FROM: null,
          AdditionalCost_TO: response.data.AdditionalCost,
          LinguistPay_FROM: null,
          LinguistPay_TO: response.data.LinguistPay,
          Profit_FROM: null,
          Profit_TO: response.data.Profit,
          Margin_FROM: null,
          Margin_TO: response.data.Margin,
          InterpretationType_FROM: null,
          InterpretationType_TO: response.data.InterpretationType,
          Mode_FROM: null,
          Mode_TO: response.data.Mode,
          Address_FROM: null,
          Address_TO: response.data.Address,
          MileageRoundtrip_FROM: null,
          MileageRoundtrip_TO: response.data.MileageRoundtrip,
          MileageCost_FROM: null,
          MileageCost_TO: response.data.MileageCost,
          ParkingFeesTolls_FROM: null,
          ParkingFeesTolls_TO: response.data.ParkingFeesTolls,
          PreparationTime_FROM: null,
          PreparationTime_TO: response.data.PreparationTime,
          SchedulingTime_FROM: null,
          SchedulingTime_TO: response.data.SchedulingTime,
          ActualStart_FROM: null,
          ActualStart_TO: response.data.ActualStart,
          ActualEnd_FROM: null,
          ActualEnd_TO: response.data.ActualEnd,
          InvoiceTo_FROM: null,
          InvoiceTo_TO: response.data.InvoiceTo,
          Paid_FROM: null,
          Paid_TO: response.data.Paid,
          Rate_FROM: null,
          Rate_TO: response.data.Rate,
          RateSimultaneous_FROM: null,
          RateSimultaneous_TO: response.data.RateSimultaneous,
          ServiceClientRate_FROM: null,
          ServiceClientRate_TO: response.data.ServiceClientRate,
          ServiceClientRateSimultaneous_FROM: null,
          ServiceClientRateSimultaneous_TO:
            response.data.ServiceClientRateSimultaneous,
          CancellationReason_FROM: null,
          CancellationReason_TO: response.data.CancellationReason,
          TargetLanguagesIDs_FROM: null,
          TargetLanguagesIDs_TO: [language],
          ChangedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          ContactPersonName_FROM: null,
          ContactPersonName_TO: response.data.ContactPersonName,
          ContactPersonNumber_FROM: null,
          ContactPersonNumber_TO: response.data.ContactPersonNumber,
        };
        void dataHistory;

        if (isFinal) {
          setIsSavingChanges(false);

          triggerPageRefresh(
            length + " New Interpretations Added Successfully!",
            theme,
          );

          releaseLock();
          setOverlayName("");
        }
      })
      .catch((err) => console.log(err));
  }

  function addNewInterpretation() {
    setIsSavingChanges(true);
    setIsEditable(false);

    const data = {
      Assignment: {
        id: 0,
        Date: date && date.isValid() ? date.format("YYYY-MM-DD") : null,
        ProjectNumber: projectNumber,
        CheckingDate: null,
        TimeStart: (timeStart && timeStart.format("HH:mm:ss")) || "00:00:00",
        TimeEnd: (timeEnd && timeEnd.format("HH:mm:ss")) || "00:00:00",
        ServiceCategory: "Interpretation",
        TotalDuration:
          (totalDuration && totalDuration.format("HH:mm:ss")) || "00:00:00",
        Duration: (duration && duration.format("HH:mm:ss")) || "00:00:00",
        ActualDuration:
          (actualDuration && actualDuration.format("HH:mm:ss")) || "00:00:00",
        Name: name,
        Details: details,
        InterpretationStatus:
          interpretationStatus === "" ? null : interpretationStatus,
        AccountCharge: accountCharge === "" ? null : accountCharge,
        TotalCost: totalCost === "" ? null : totalCost,
        AdditionalCost: additionalCost === "" ? null : additionalCost,
        LinguistPay: linguistPay === "" ? null : linguistPay,
        Profit: profit === "" ? null : profit,
        Margin: margin === "" ? null : margin,
        InterpretationType: interpretationType,
        Mode: mode === "" ? null : mode,
        Address: address,
        MileageRoundtrip: mileageRoundtrip === "" ? null : mileageRoundtrip,
        MileageCost: mileageCost === "" ? null : mileageCost,
        ParkingFeesTolls: parkingFeesTolls === "" ? null : parkingFeesTolls,
        PreparationTime:
          (preparationTime && preparationTime.format("HH:mm:ss")) || "00:00:00",
        SchedulingTime:
          (schedulingTime && schedulingTime.format("HH:mm:ss")) || "00:00:00",
        ActualStart:
          (actualStart && actualStart.format("HH:mm:ss")) || "00:00:00",
        ActualEnd: (actualEnd && actualEnd.format("HH:mm:ss")) || "00:00:00",
        Paid: false,
        Rate: rate === "" ? null : rate,
        RateSimultaneous: rateSimultaneous === "" ? null : rateSimultaneous,
        ServiceClientRate:
          clientRate === "" ? null : Number(clientRate).toFixed(2),
        ServiceClientRateSimultaneous:
          clientRateSimultaneous === ""
            ? null
            : Number(clientRateSimultaneous).toFixed(2),
        TotalLinguistPay: totalLinguistPay === "" ? null : totalLinguistPay,
        EquipmentCost: equipmentCost === "" ? null : equipmentCost,
        AdditionalLinguistCost:
          additionalLinguistCost === "" ? null : additionalLinguistCost,
        DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
        ApplyTwoHourMinimum: twoHoursMin,
        IsShortAssignment: allowShortAssignments,
        Deleted: false,
        Cancelled:
          interpretationStatus !== null
            ? interpretationStatus.startsWith("Cancel")
            : false,
        Invoiced: false,
        CancellationReason:
          cancellationReason === "" ? null : cancellationReason,
        CreatedBy: currentUser.id,
        CheckedBy: null,
        ContactPersonName: contactPersonName,
        ContactPersonNumber: contactPersonNumber,
      },
      Language: language,
      Client: client,
      Requestor: requestor,
      Linguist: linguist === "" ? null : linguist,
      Invoicer: invoiceTo === "" ? null : invoiceTo,
    };

    api
      .post("/services/interpretations/addNewInterpretation", {
        data: data,
      })
      .then((response) => {
        if (note) addNewNote(response.data.id);

        const dataHistory = {
          Status: "Created",
          ServiceID: response.data.id,
          ProjectNumber: response.data.ProjectNumber,
          Date_FROM: null,
          Date_TO: response.data.Date,
          ClientID_FROM: null,
          ClientID_TO: response.data.ClientID,
          RequestorID_FROM: null,
          RequestorID_TO: response.data.RequestorID,
          LinguistID_FROM: null,
          LinguistID_TO: response.data.LinguistID,
          ChangedBy: currentUser.id,
          TimeStart_FROM: null,
          TimeStart_TO: response.data.TimeStart,
          TimeEnd_FROM: null,
          TimeEnd_TO: response.data.TimeEnd,
          TotalDuration_FROM: null,
          TotalDuration_TO: response.data.TotalDuration,
          Duration_FROM: null,
          Duration_TO: response.data.Duration,
          ActualDuration_FROM: null,
          ActualDuration_TO: response.data.ActualDuration,
          Name_FROM: null,
          Name_TO: response.data.Name,
          Details_FROM: null,
          Details_TO: response.data.Details,
          InterpretationStatus_FROM: null,
          InterpretationStatus_TO: response.data.InterpretationStatus,
          AccountCharge_FROM: null,
          AccountCharge_TO: response.data.AccountCharge,
          TotalCost_FROM: null,
          TotalCost_TO: response.data.TotalCost,
          AdditionalCost_FROM: null,
          AdditionalCost_TO: response.data.AdditionalCost,
          LinguistPay_FROM: null,
          LinguistPay_TO: response.data.LinguistPay,
          Profit_FROM: null,
          Profit_TO: response.data.Profit,
          Margin_FROM: null,
          Margin_TO: response.data.Margin,
          InterpretationType_FROM: null,
          InterpretationType_TO: response.data.InterpretationType,
          Mode_FROM: null,
          Mode_TO: response.data.Mode,
          Address_FROM: null,
          Address_TO: response.data.Address,
          MileageRoundtrip_FROM: null,
          MileageRoundtrip_TO: response.data.MileageRoundtrip,
          MileageCost_FROM: null,
          MileageCost_TO: response.data.MileageCost,
          ParkingFeesTolls_FROM: null,
          ParkingFeesTolls_TO: response.data.ParkingFeesTolls,
          PreparationTime_FROM: null,
          PreparationTime_TO: response.data.PreparationTime,
          SchedulingTime_FROM: null,
          SchedulingTime_TO: response.data.SchedulingTime,
          ActualStart_FROM: null,
          ActualStart_TO: response.data.ActualStart,
          ActualEnd_FROM: null,
          ActualEnd_TO: response.data.ActualEnd,
          InvoiceTo_FROM: null,
          InvoiceTo_TO: response.data.InvoiceTo,
          Paid_FROM: null,
          Paid_TO: response.data.Paid,
          Rate_FROM: null,
          Rate_TO: response.data.Rate,
          RateSimultaneous_FROM: null,
          RateSimultaneous_TO: response.data.RateSimultaneous,
          ServiceClientRate_FROM: null,
          ServiceClientRate_TO: response.data.ServiceClientRate,
          ServiceClientRateSimultaneous_FROM: null,
          ServiceClientRateSimultaneous_TO:
            response.data.ServiceClientRateSimultaneous,
          TotalLinguistPay_FROM: null,
          TotalLinguistPay_TO: response.data.TotalLinguistPay,
          EquipmentCost_FROM: null,
          EquipmentCost_TO: response.data.EquipmentCost,
          AdditionalLinguistCost_FROM: null,
          AdditionalLinguistCost_TO: response.data.AdditionalLinguistCost,
          CancellationReason_FROM: null,
          CancellationReason_TO: response.data.CancellationReason,
          DisableAutoChargeCalc_FROM: null,
          DisableAutoChargeCalc_TO: response.data.DisableAutoChargeCalc,
          ApplyTwoHourMinimum_FROM: null,
          ApplyTwoHourMinimum_TO: response.data.ApplyTwoHourMinimum,
          TargetLanguagesIDs_FROM: null,
          TargetLanguagesIDs_TO: [language],
          ChangedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          ContactPersonName_FROM: null,
          ContactPersonName_TO: response.data.ContactPersonName,
          ContactPersonNumber_FROM: null,
          ContactPersonNumber_TO: response.data.ContactPersonNumber,
        };
        void dataHistory;

        setIsSavingChanges(false);

        triggerPageRefresh("New Interpretation Added Successfully!", theme);

        // Emit assignment saved event for other viewers
        if (socket) {
          socket.emit("assignment:saved", {
            modelType: "Interpretation",
            recordId: response.data.ServiceID || response.data.id,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
            projectNumber: response.data.ProjectNumber,
          });

          // Also notify the interpretations list page to refetch
          socket.emit("assignment:saved", {
            modelType: "InterpretationsList",
            recordId: "all",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
            projectNumber: response.data.ProjectNumber,
          });
        }

        setOverlayName("");
      })
      .catch((err) => console.log(err));
  }

  function updateInterpretation() {
    setIsSavingChanges(true);
    setIsEditable(false);

    const dataOld = {
      Assignment: {
        id: overlayData.id,
        Date: overlayData.Date,
        ProjectNumber: overlayData.ProjectNumber,
        CheckingDate: null,
        TimeStart: overlayData.TimeStart,
        TimeEnd: overlayData.TimeEnd,
        ServiceCategory: "Interpretation",
        TotalDuration: overlayData.TotalDuration,
        Duration: overlayData.Duration,
        ActualDuration: overlayData.ActualDuration,
        Name: overlayData.Name,
        Details: overlayData.Details,
        ClientRateDiscount: overlayData.ClientRateDiscount || 0,
        InterpretationStatus:
          overlayData.InterpretationStatus === ""
            ? null
            : overlayData.InterpretationStatus,
        AccountCharge:
          overlayData.AccountCharge === "" ? null : overlayData.AccountCharge,
        TotalCost: overlayData.TotalCost === "" ? null : overlayData.TotalCost,
        AdditionalCost:
          overlayData.AdditionalCost === "" ||
          overlayData.AdditionalCost === null
            ? "0.00"
            : overlayData.AdditionalCost,
        LinguistPay:
          overlayData.LinguistPay === "" ? null : overlayData.LinguistPay,
        Profit: overlayData.Profit === "" ? null : overlayData.Profit,
        Margin: overlayData.Margin === "" ? null : overlayData.Margin,
        InterpretationType:
          overlayData.InterpretationType === ""
            ? null
            : overlayData.InterpretationType,
        Mode:
          overlayData.Mode === "" || overlayData.Mode === null
            ? "Consecutive"
            : overlayData.Mode,
        Address: overlayData.Address,
        MileageRoundtrip:
          overlayData.MileageRoundtrip === "" ||
          overlayData.MileageRoundtrip === null
            ? "0.00"
            : overlayData.MileageRoundtrip,
        MileageCost:
          overlayData.MileageCost === "" ? null : overlayData.MileageCost,
        ParkingFeesTolls:
          overlayData.ParkingFeesTolls === ""
            ? null
            : overlayData.ParkingFeesTolls,
        PreparationTime: overlayData.PreparationTime,
        SchedulingTime: overlayData.SchedulingTime,
        ActualStart: overlayData.ActualStart,
        ActualEnd: overlayData.ActualEnd,
        Paid: false,
        Rate: overlayData.Rate === "" ? null : overlayData.Rate,
        RateSimultaneous:
          overlayData.RateSimultaneous === ""
            ? null
            : overlayData.RateSimultaneous,
        ServiceClientRate:
          overlayData.ServiceClientRate === ""
            ? null
            : overlayData.ServiceClientRate,
        ServiceClientRateSimultaneous:
          overlayData.ServiceClientRateSimultaneous === ""
            ? null
            : overlayData.ServiceClientRateSimultaneous,
        TotalLinguistPay:
          overlayData.TotalLinguistPay === ""
            ? null
            : overlayData.TotalLinguistPay,
        EquipmentCost:
          overlayData.EquipmentCost === "" ? null : overlayData.EquipmentCost,
        AdditionalLinguistCost:
          overlayData.AdditionalLinguistCost === ""
            ? null
            : overlayData.AdditionalLinguistCost,
        DisableAutoChargeCalc: overlayData.DisableAutoChargeCalc || false,
        ApplyTwoHourMinimum: overlayData.ApplyTwoHourMinimum || false,
        IsShortAssignment: overlayData.IsShortAssignment || false,
        Deleted: false,
        Cancelled: overlayData.Cancelled,
        Invoiced: false,
        CancellationReason: overlayData.CancellationReason,
        CreatedBy: currentUser.id,
        CheckedBy: null,
        ContactPersonName: overlayData.ContactPersonName,
        ContactPersonNumber: overlayData.ContactPersonNumber,
      },
      Language: overlayData.ServiceTargetLanguages[0].Language,
      Client: overlayData.Client,
      Requestor: overlayData.Requestor,
      Linguist: overlayData.Linguist,
      Invoicer: overlayData.Invoicer,
    };

    const dataNew = {
      Assignment: {
        id: overlayData.id,
        Date: date.format("YYYY-MM-DD"),
        ProjectNumber: projectNumber,
        //date.format("YYYY-MM-DD") === overlayData.Date
        //  ? overlayData.ProjectNumber
        //  : projectNumber,
        CheckingDate: null,
        TimeStart: (timeStart && timeStart.format("HH:mm:ss")) || "00:00:00",
        TimeEnd: (timeEnd && timeEnd.format("HH:mm:ss")) || "00:00:00",
        ServiceCategory: "Interpretation",
        TotalDuration:
          (totalDuration && totalDuration.format("HH:mm:ss")) || "00:00:00",
        Duration: duration.format("HH:mm:ss") || "00:00:00",
        ActualDuration:
          (actualDuration && actualDuration.format("HH:mm:ss")) || "00:00:00",
        Name: name,
        ClientRateDiscount: Number(clientRateDiscount).toFixed(2),
        Details: details,
        InterpretationStatus:
          interpretationStatus === "" ? null : interpretationStatus,
        AccountCharge:
          accountCharge === "" ? null : Number(accountCharge).toFixed(2),
        TotalCost: totalCost === "" ? null : Number(totalCost).toFixed(2),
        AdditionalCost:
          additionalCost === "" ? null : Number(additionalCost).toFixed(2),
        LinguistPay: linguistPay === "" ? null : Number(linguistPay).toFixed(2),
        Profit: profit === "" ? null : Number(profit).toFixed(2),
        Margin: margin === "" ? null : Number(margin).toFixed(2),
        InterpretationType: interpretationType,
        Mode: mode === "" ? null : mode,
        Address: address,
        MileageRoundtrip: mileageRoundtrip === "" ? null : mileageRoundtrip,
        MileageCost: mileageCost === "" ? null : mileageCost,
        ParkingFeesTolls: parkingFeesTolls === "" ? null : parkingFeesTolls,
        PreparationTime:
          (preparationTime && preparationTime.format("HH:mm:ss")) || "00:00:00",
        SchedulingTime:
          (schedulingTime && schedulingTime.format("HH:mm:ss")) || "00:00:00",
        ActualStart:
          (actualStart && actualStart.format("HH:mm:ss")) || "00:00:00",
        ActualEnd: (actualEnd && actualEnd.format("HH:mm:ss")) || "00:00:00",
        Paid: false,
        Rate: rate === "" ? null : Number(rate).toFixed(2),
        RateSimultaneous:
          rateSimultaneous === "" ? null : Number(rateSimultaneous).toFixed(2),
        ServiceClientRate:
          clientRate === "" ? null : Number(clientRate).toFixed(2),
        ServiceClientRateSimultaneous:
          clientRateSimultaneous === ""
            ? null
            : Number(clientRateSimultaneous).toFixed(2),
        TotalLinguistPay:
          totalLinguistPay === "" ? null : Number(totalLinguistPay).toFixed(2),
        EquipmentCost:
          equipmentCost === "" ? null : Number(equipmentCost).toFixed(2),
        AdditionalLinguistCost:
          additionalLinguistCost === ""
            ? null
            : Number(additionalLinguistCost).toFixed(2),
        DisableAutoChargeCalc: disableAutomaticCalculationOfCharges,
        ApplyTwoHourMinimum: twoHoursMin,
        IsShortAssignment: allowShortAssignments,
        Deleted: false,
        Cancelled:
          interpretationStatus !== null
            ? interpretationStatus.startsWith("Cancel")
            : false,
        Invoiced: false,
        CancellationReason:
          cancellationReason === "" ? null : cancellationReason,
        CreatedBy: currentUser.id,
        CheckedBy: null,
        ContactPersonName: contactPersonName,
        ContactPersonNumber: contactPersonNumber,
      },
      Language: language,
      Client: client,
      Requestor: requestor,
      Linguist: linguist,
      Invoicer: invoiceTo,
    };

    const allData = {
      Old: dataOld,
      New: dataNew,
    };

    api
      .post("/services/interpretations/updateInterpretation", {
        data: allData,
      })
      .then((response) => {
        if (note) addNewNote(overlayData.id);

        setIsSavingChanges(false);
        triggerPageRefresh(
          "Interpretation Assignment Updated Sucessfully",
          theme,
        );

        // Emit assignment saved event for other viewers
        if (socket) {
          socket.emit("assignment:saved", {
            modelType: "Interpretation",
            recordId: overlayData.id,
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
            projectNumber: overlayData.ProjectNumber,
          });

          // Also notify the interpretations list page to refetch
          socket.emit("assignment:saved", {
            modelType: "InterpretationsList",
            recordId: "all",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
            projectNumber: overlayData.ProjectNumber,
          });
        }

        releaseLock();
        setOverlayName("");
      })
      .catch((err) => console.log(err));
  }

  function addNewNote(id) {
    const data = {
      Note: note,
      ServiceID: id,
      CreatedBy: currentUser.id,
      CreatedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      IsLatest: 1,
    };

    api
      .post("/services/addNewNote", {
        data: data,
      })
      .then((response) => {
        const dataHistory = {
          Status: "Created",
          ServiceID: overlayData.id,
          Note_FROM: null,
          Note_TO: response.data.id,
          ChangedBy: currentUser.id,
          ChangedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        };
        void dataHistory;

        //ADDING THE HISTORY
        /*
        api
          .post("/serviceHistories/addNoteHistory", {
            data: dataHistory,
          })
          .then((response) => {})
          .catch((err) => {
            console.log(err);
          });
        */
      })
      .catch((err) => console.log(err));
  }

  // Fetch mileage rates on component mount
  useEffect(() => {
    const fetchMileageRates = async () => {
      try {
        const response = await api.get("/mileageRates");
        setMileageRates(response.data || []);
      } catch (err) {
        console.error("Error fetching mileage rates:", err);
        // Fallback to default 0.7 if fetch fails
      }
    };

    fetchMileageRates();
  }, []);

  addBulkInterpretationsRef.current = addBulkInterpretations;
  addNewInterpretationRef.current = addNewInterpretation;
  updateInterpretationRef.current = updateInterpretation;

  useEffect(() => {
    if (shouldValidate) {
      if (
        !(
          dateError ||
          projectNumberError ||
          nameError ||
          interpretationTypeError ||
          clientError ||
          clientRateError ||
          requestorError ||
          languageError ||
          linguistError ||
          rateError ||
          addressError ||
          interpretationStatusError ||
          timeStartError ||
          timeEndError ||
          accountChargeError ||
          totalCostError ||
          linguistPayError ||
          detailsError
        )
      ) {
        isNewAssignment
          ? showRecurring
            ? addBulkInterpretationsRef.current()
            : addNewInterpretationRef.current()
          : updateInterpretationRef.current();
      }

      setShouldValidate(false);
    }
  }, [
    shouldValidate,
    nameError,
    dateError,
    projectNumberError,
    interpretationTypeError,
    clientError,
    clientRateError,
    requestorError,
    languageError,
    linguistError,
    rateError,
    addressError,
    interpretationStatusError,
    timeStartError,
    timeEndError,
    accountChargeError,
    totalCostError,
    linguistPayError,
    detailsError,
    isNewAssignment,
    showRecurring,
  ]);

  useEffect(() => {
    updateCurrentProgressRef.current();
  }, [
    name,
    date,
    projectNumber,
    interpretationType,
    client,
    clientRate,
    requestor,
    language,
    linguist,
    rate,
    address,
    interpretationStatus,
    timeStart,
    timeEnd,
    accountCharge,
    totalCost,
    linguistPay,
    details,
  ]);

  useEffect(() => {
    if (!isNewAssignment) {
      initialDataRef.current = {
        name: overlayData.Name,
        date: overlayData.Date, // assuming string format "YYYY-MM-DD"
        projectNumber: overlayData.ProjectNumber,
        interpretationType: overlayData.InterpretationType,
        client: overlayData.Client, // if client is an object, consider comparing by id
        requestor: overlayData.Requestor, // comparing by Name (or id if available)
        invoiceTo: overlayData.Invoicer,
        language: overlayData.ServiceTargetLanguages?.[0]?.Language,
        linguist: overlayData.Linguist,
        address: overlayData.Address,
        interpretationStatus: overlayData.InterpretationStatus,
        cancellationReason: overlayData.CancellationReason,
        accountCharge: overlayData.AccountCharge,
        totalCost: overlayData.TotalCost,
        linguistPay: overlayData.LinguistPay,
        additionalCost: overlayData.AdditionalCost,
        mode: overlayData.Mode,
        rate: overlayData.Rate,
        clientRate: initialClientRate, // see your initialization of initialClientRate
        timeStart: overlayData.TimeStart,
        timeEnd: overlayData.TimeEnd,
      };
    }
  }, [isNewAssignment, initialClientRate, overlayData]);

  useEffect(() => {
    if (!isNewAssignment && initialDataRef.current) {
      const dirty =
        name !== initialDataRef.current.name ||
        (date && date.format("YYYY-MM-DD")) !== initialDataRef.current.date ||
        projectNumber !== initialDataRef.current.projectNumber ||
        interpretationType !== initialDataRef.current.interpretationType ||
        client?.Account !== initialDataRef.current.client?.Account ||
        requestor?.Name !== initialDataRef.current.requestor?.Name ||
        invoiceTo?.Name !== initialDataRef.current.invoiceTo?.Name ||
        language?.Name !== initialDataRef.current.language?.Name ||
        linguist?.Name !== initialDataRef.current.linguist?.Name ||
        address !== initialDataRef.current.address ||
        interpretationStatus !== initialDataRef.current.interpretationStatus || //Error here
        (cancellationReason || "") !==
          (initialDataRef.current.cancellationReason || "") ||
        Number(accountCharge) !==
          Number(initialDataRef.current.accountCharge) ||
        Number(totalCost) !== Number(initialDataRef.current.totalCost) ||
        Number(linguistPay) !== Number(initialDataRef.current.linguistPay) ||
        Number(additionalCost) !==
          Number(initialDataRef.current.additionalCost) ||
        mode !== initialDataRef.current.mode ||
        Number(rate) !== Number(initialDataRef.current.rate) ||
        Number(clientRate) !== Number(initialDataRef.current.clientRate) ||
        Number(clientRateSimultaneous) !==
          Number(initialDataRef.current.clientRateSimultaneous || "") ||
        (timeStart && timeStart.format("HH:mm:ss")) !==
          initialDataRef.current.timeStart ||
        (timeEnd && timeEnd.format("HH:mm:ss")) !==
          initialDataRef.current.timeEnd ||
        ((actualStart && actualStart.format("HH:mm:ss")) || "") !==
          (initialDataRef.current.actualStart || "") ||
        ((actualEnd && actualEnd.format("HH:mm:ss")) || "") !==
          (initialDataRef.current.actualEnd || "");
      setHasChanges(dirty);
    }
  }, [
    name,
    date,
    projectNumber,
    interpretationType,
    client,
    requestor,
    invoiceTo,
    language,
    linguist,
    address,
    interpretationStatus,
    cancellationReason,
    accountCharge,
    totalCost,
    linguistPay,
    additionalCost,
    mode,
    rate,
    clientRate,
    clientRateSimultaneous,
    timeStart,
    timeEnd,
    actualStart,
    actualEnd,
    isNewAssignment,
  ]);

  const saveInterpretation = () => {
    // Check if we need to show paid warning
    if (!pendingSave && checkPaidWarning()) {
      setShowPaidWarning(true);
      return;
    }

    // If we reach here, either no warning needed or user confirmed
    setPendingSave(false);
    validateFields();
    setShouldValidate(true);
  };

  const attemptSave = () => {
    if (isSavingChanges || isLocked) {
      return;
    }

    if (isNewAssignment && !triggerMissingFieldsShake()) {
      return;
    }

    saveInterpretation();
  };
  saveInterpretationRef.current = saveInterpretation;
  attemptSaveRef.current = attemptSave;

  // Handle confirmation of paid assignment warning
  const handlePaidWarningConfirm = () => {
    setShowPaidWarning(false);
    setPendingSave(true);
    // Trigger save again, but this time with pendingSave = true to bypass the check
    setTimeout(() => saveInterpretation(), 0);
  };

  // Handle cancellation of paid assignment warning
  const handlePaidWarningCancel = () => {
    setShowPaidWarning(false);
    setPendingSave(false);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUploadClick = () => {
    const data = {
      Action: OVERLAY_NAMES.INTERPRETATION_ACTION_OPI_UPLOAD,

      Interpretations: overlayData.Interpretations,
      ClientsOPIMinimums: overlayData.ClientsOPIMinimums,
      Languages: overlayData.Languages,
      Linguists: overlayData.Linguists,
      Clients: overlayData.Clients,
    };

    setOverlayData(data);

    setOverlayName(OVERLAY_NAMES.INTERPRETATION_OPI_UPLOAD);

    handleMenuClose();
  };

  useEffect(() => {
    const handleEnterSubmit = (event) => {
      const target = event.target;
      const tagName = target?.tagName;
      const isTextArea = tagName === "TEXTAREA";
      const isContentEditable = !!target?.isContentEditable;
      const isInsideDialog =
        target instanceof Element && !!target.closest('[role="dialog"]');

      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !isTextArea &&
        !isContentEditable &&
        !isInsideDialog
      ) {
        event.preventDefault();
        attemptSaveRef.current();
      }
    };

    window.addEventListener("keydown", handleEnterSubmit);
    return () => window.removeEventListener("keydown", handleEnterSubmit);
  }, []);

  // Calculate charges automatically when dependencies change
  useEffect(() => {
    // Skip automatic calculation if disabled
    if (disableAutomaticCalculationOfCharges) {
      return;
    }

    if (
      [
        "Cancelled (With 24 Hrs Notice)",
        "Cancelled (Unable To Fill)",
        //"Cancelled (Re-schedule)",
      ].includes(interpretationStatus)
    ) {
      cancelCharges();
      return;
    }

    if (!totalDuration) return;

    // Helper to get decimal hours from a dayjs duration
    const toDecimal = (d) => (d && d.isValid ? d.hour() + d.minute() / 60 : 0);

    // Calculate durations
    const totalDecimal = toDecimal(totalDuration);
    const prepDecimal = toDecimal(preparationTime);
    const schedDecimal = toDecimal(schedulingTime);

    let durationDecimal = totalDecimal;
    const minHours = twoHoursMin ? 2 : 1;

    // Round to nearest quarter hour FIRST (normalize measured duration)
    durationDecimal = Math.ceil(durationDecimal * 4) / 4;

    // Then apply minimum hours only if short assignments are not allowed
    if (!allowShortAssignments) {
      if (durationDecimal <= 2 && minHours > 1) {
        durationDecimal = minHours;
      }
      if (minHours === 1 && durationDecimal < 1) {
        durationDecimal = minHours;
      }
    }

    let pay = 0;
    let charge = 0;
    let chargeText = "";
    let totalLingPay = 0;
    let totalLingPayText = "";

    if (mode === "Simultaneous") {
      // Calculate the "core" time (total - prep - sched), never negative
      const coreDecimal = Math.max(
        durationDecimal - prepDecimal - schedDecimal,
        0,
      );

      // Linguist pay: rate × (prep + sched) + rateSimultaneous × coreDecimal
      const rateVal = parseFloat(rate || 0);
      const rateSimultaneousVal = parseFloat(rateSimultaneous || 0);

      pay =
        rateVal * (prepDecimal + schedDecimal) +
        rateSimultaneousVal * coreDecimal;

      // TotalLinguistPay = rate × (prep + sched) + rateSimultaneous × coreDecimal + Mileage + Parking + AdditionalLinguistCost
      totalLingPay =
        rateVal * (prepDecimal + schedDecimal) +
        rateSimultaneousVal * coreDecimal +
        parseFloat(mileageCost || 0) +
        parseFloat(parkingFeesTolls || 0) +
        parseFloat(additionalLinguistCost || 0);

      totalLingPayText = `${rateSimultaneousVal.toFixed(2)} x ${coreDecimal.toFixed(2)} +  ${rateVal.toFixed(2)} x [${prepDecimal.toFixed(2)} + ${schedDecimal.toFixed(2)}] + ${parseFloat(mileageCost || 0).toFixed(2)} + ${parseFloat(parkingFeesTolls || 0).toFixed(2)} + ${parseFloat(additionalLinguistCost || 0).toFixed(2)}`;

      // Client charge - use simultaneous rate for core time, consecutive rate for prep/scheduling
      const clientRateSimultaneousAdj =
        (clientRateSimultaneous / 100) * (100 - clientRateDiscount) || 0;
      const clientRateAdj =
        (clientRate / 100) * (100 - clientRateDiscount) || 0;

      charge =
        clientRateSimultaneousAdj * coreDecimal +
        clientRateAdj * (prepDecimal + schedDecimal) +
        parseFloat(mileageCost || 0) +
        parseFloat(parkingFeesTolls || 0) +
        parseFloat(additionalLinguistCost || 0) +
        parseFloat(additionalCost || 0) +
        parseFloat(equipmentCost || 0);

      chargeText =
        `( ${clientRateSimultaneousAdj.toFixed(2)} x ${coreDecimal.toFixed(2)} + ${clientRateAdj.toFixed(2)} x [${schedDecimal.toFixed(
          2,
        )} + ${prepDecimal.toFixed(2)}] )` +
        " + " +
        parseFloat(mileageCost || 0).toFixed(2) +
        " + " +
        parseFloat(parkingFeesTolls || 0).toFixed(2) +
        " + " +
        parseFloat(additionalLinguistCost || 0).toFixed(2) +
        " + " +
        parseFloat(additionalCost || 0).toFixed(2) +
        " + " +
        parseFloat(equipmentCost || 0).toFixed(2);
    } else {
      // Consecutive: all time at 1x
      pay = parseFloat(rate || 0) * parseFloat(durationDecimal || 0);

      // TotalLinguistPay = Rate × totalTime + Mileage + Parking + AdditionalLinguistCost
      const rateVal = parseFloat(rate || 0);
      totalLingPay =
        rateVal * parseFloat(durationDecimal || 0) +
        parseFloat(mileageCost || 0) +
        parseFloat(parkingFeesTolls || 0) +
        parseFloat(additionalLinguistCost || 0);

      totalLingPayText = `${rateVal.toFixed(2)} x ${parseFloat(durationDecimal || 0).toFixed(2)} + ${parseFloat(mileageCost || 0).toFixed(2)} + ${parseFloat(parkingFeesTolls || 0).toFixed(2)} + ${parseFloat(additionalLinguistCost || 0).toFixed(2)}`;

      const clientRateAdj =
        (clientRate / 100) * (100 - clientRateDiscount) || 0;
      charge =
        clientRateAdj * parseFloat(durationDecimal || 0) +
        parseFloat(mileageCost || 0) +
        parseFloat(parkingFeesTolls || 0) +
        parseFloat(additionalLinguistCost || 0) +
        parseFloat(additionalCost || 0) +
        parseFloat(equipmentCost || 0);

      chargeText =
        `( ${clientRateAdj.toFixed(2)} x ${parseFloat(
          durationDecimal || 0,
        ).toFixed(2)} )` +
        " + " +
        parseFloat(mileageCost || 0).toFixed(2) +
        " + " +
        parseFloat(parkingFeesTolls || 0).toFixed(2) +
        " + " +
        parseFloat(additionalLinguistCost || 0).toFixed(2) +
        " + " +
        parseFloat(additionalCost || 0).toFixed(2) +
        " + " +
        parseFloat(equipmentCost || 0).toFixed(2);
    }

    // Calculate new TotalCost: TotalLinguistPay + AdditionalCost (no EquipmentCost, that's only charged to client)
    const totCost = totalLingPay + parseFloat(additionalCost || 0);

    const totCostText = `${totalLingPay.toFixed(2)} + ${parseFloat(additionalCost || 0).toFixed(2)}`;

    const prof = parseFloat(charge) - parseFloat(totCost);

    const newPay = pay.toFixed(2);
    if (newPay !== linguistPay) {
      setLinguistPay(newPay);
    }

    // Update TotalLinguistPay
    const newTotalLingPay = totalLingPay.toFixed(2);
    if (newTotalLingPay !== totalLinguistPay) {
      setTotalLinguistPay(newTotalLingPay);
    }

    setAccountChargeHelperText(chargeText);
    setTotalCostHelperText(totCostText);
    // Set TotalLinguistPay helper text if it exists
    if (typeof totalLingPayText !== "undefined") {
      setTotalLinguistPayHelperText(totalLingPayText);
    }
    // Update calculated values
    setTotalCost(totCost.toFixed(2));
    setAccountCharge(charge.toFixed(2));
    setProfit(prof.toFixed(2));
    // Only calculate margin if charge is not zero
    if (parseFloat(charge) === 0) {
      setMargin(0);
    } else {
      const calculatedMargin = (parseFloat(prof) / parseFloat(charge)) * 100;
      setMargin(
        isNaN(calculatedMargin) || !isFinite(calculatedMargin)
          ? 0
          : calculatedMargin.toFixed(2),
      );
    }
  }, [
    interpretationStatus,
    totalDuration,
    clientRate,
    clientRateSimultaneous,
    rate,
    rateSimultaneous,
    parkingFeesTolls,
    additionalCost,
    additionalLinguistCost,
    equipmentCost,
    mileageCost,
    mode,
    twoHoursMin,
    clientRateDiscount,
    linguistPay,
    preparationTime,
    schedulingTime,
    disableAutomaticCalculationOfCharges,
    allowShortAssignments,
    totalLinguistPay,
  ]);

  // In manual mode, keep Total Cost and Total Linguist Pay aligned with Linguist Pay.
  useEffect(() => {
    if (!disableAutomaticCalculationOfCharges) {
      return;
    }

    if (totalCost !== linguistPay) {
      setTotalCost(linguistPay);
    }

    if (totalLinguistPay !== linguistPay) {
      setTotalLinguistPay(linguistPay);
    }
  }, [
    disableAutomaticCalculationOfCharges,
    linguistPay,
    totalCost,
    totalLinguistPay,
  ]);

  // Recalculate profit and margin whenever accountCharge or totalCost changes
  useEffect(() => {
    const charge = parseFloat(accountCharge || 0);
    const totCost = parseFloat(totalCost || 0);
    const prof = charge - totCost;

    setProfit(prof.toFixed(2));
    // Only calculate margin if charge is not zero
    if (charge === 0) {
      setMargin(0);
    } else {
      const calculatedMargin = (prof / charge) * 100;
      setMargin(
        isNaN(calculatedMargin) || !isFinite(calculatedMargin)
          ? 0
          : calculatedMargin.toFixed(2),
      );
    }
  }, [accountCharge, totalCost]);

  // Update mileage cost when mileage roundtrip or date changes
  useEffect(() => {
    if (mileageRoundtrip && date && date.isValid()) {
      const year = date.year();
      const mileageRate = getMileageRateForYear(year);
      const calculatedCost = (
        parseFloat(mileageRoundtrip) * mileageRate
      ).toFixed(2);
      setMileageCost(calculatedCost);
    }
  }, [mileageRoundtrip, date, getMileageRateForYear]);

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          bgcolor: theme.palette.background.alt,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          height: { xs: "95%", sm: "75%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "1200px",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
            flexDirection: "row",
            p: ".75rem 2rem",
          }}
        >
          {/* Left side */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              flexDirection: "row",
              //gap: 1,
            }}
          >
            <IconButton
              color="primary"
              size="large"
              onClick={() => {
                releaseLock();
                setOverlayName("");
              }}
            >
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {isNewAssignment
                ? "New Interpretation Assignment"
                : completing
                  ? "Complete Interpretation Assignment"
                  : "Edit Interpretation Assignment:"}
            </Typography>
          </Box>

          {/* Right side: 3 dots menu */}
          {isNewAssignment && (
            <IconButton
              onClick={handleMenuOpen}
              size="large" // makes the button bigger
              sx={{ alignSelf: "center" }} // centers it vertically in the flex container
            >
              <MoreVertIcon fontSize="inherit" />{" "}
              {/* matches the button size */}
            </IconButton>
          )}

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={handleUploadClick}>
              <ListItemIcon>
                <UploadFileIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Upload OPI Logs</ListItemText>
            </MenuItem>
          </Menu>
        </Box>

        <Box
          sx={{
            position: "relative",
            flex: 1, // ✅ Takes remaining space in flex container
            minHeight: 0, // ✅ Allows shrinking in flex
            width: "100%",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "50%",
              backgroundColor: selectedDateHoliday
                ? theme.palette.background.info
                : "transparent",
              //borderRadius: "0 0 1.5rem 1.5rem",
              zIndex: 0,
            }}
          />
          <Box
            component="form"
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              p: "1rem 2.3rem 1rem 2.5rem",
              height: "100%", // Fills the parent
              overflowY: "auto",
              width: "100%",
              borderRadius: "1.5rem",
              border: `1px solid ${theme.palette.background.default}`,
              backgroundColor: theme.palette.background.default,
              "& .MuiTextField-root": {
                m: 0,
                width: "100%",
                height: "90%",
              },
            }}
          >
            <Grid container spacing={2} width="100%">
              {!completing ? (
                <Grid container size={{ xs: 12 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <MobileDatePicker
                      disabled={!isEditable}
                      id={"Date"}
                      label="Date"
                      size="small"
                      format="dddd, MMMM DD, YYYY"
                      value={date}
                      onChange={async (newValue) => {
                        setDate(newValue);
                        setDateError(newValue === null);

                        if (newValue) {
                          setRepeatUntilDate(newValue.add(1, "month"));

                          const repeatUntilDateTemp = newValue.add(
                            1,
                            period === "Month" ? "year" : "month",
                          );

                          setRepeatUntilDate(repeatUntilDateTemp);
                        }

                        const selectedDays =
                          tempSelectedDays.length === 0
                            ? [newValue?.format("ddd") || ""]
                            : tempSelectedDays;

                        handleRecursionChange(
                          newValue,
                          repeatEvery,
                          period,
                          repeatUntilDate,
                          selectedDays,
                          showRecurring,
                        );

                        if (newValue) {
                          if (
                            newValue.format("YYYY-MM-DD") === overlayData.Date
                          ) {
                            setProjectNumber(overlayData.ProjectNumber);
                          } else {
                            // Get count for this date and generate project number
                            const count = await getDateCount(newValue);
                            const projNum = generateProjectNumber(
                              newValue,
                              count,
                            );
                            setProjectNumber(projNum);
                          }
                        }
                      }}
                      //onChange={}

                      slotProps={{
                        textField: {
                          size: "small",
                          error: dateError,
                        },
                      }}
                      sx={{
                        ...getShakeSx("Date"),
                        ...getTextFieldBorderStyles(
                          getBorderColor(
                            theme,
                            isNewAssignment,
                            (date && date.format("YYYY-MM-DD")) || "",
                            overlayData.Date,
                          ),
                        ),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      id={"ProjectNumber"}
                      label="Project Number"
                      error={projectNumberError || projectNumberExists}
                      value={projectNumber}
                      onChange={(event) => {
                        setProjectNumber(event.target.value);
                        setProjectNumberError(event.target.value === null);
                        setProjectNumberExists(false);
                      }}
                      onBlur={async () => {
                        if (projectNumber && isNewAssignment) {
                          try {
                            const response = await api.get(
                              `/services/check-project-number?projectNumber=${projectNumber}`,
                            );
                            setProjectNumberExists(response.data.exists);
                          } catch (error) {
                            console.error(
                              "Error checking project number:",
                              error,
                            );
                          }
                        }
                      }}
                      helperText={
                        projectNumberExists
                          ? "This project number already exists"
                          : ""
                      }
                      sx={{
                        ...getTextFieldBorderStyles(
                          projectNumberExists
                            ? theme.palette.error.main
                            : getBorderColor(
                                theme,
                                isNewAssignment,
                                projectNumber || "",
                                overlayData.ProjectNumber,
                              ),
                        ),
                        ...getShakeSx("ProjectNumber"),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      id={"Name"}
                      label="Name"
                      error={nameError}
                      value={name}
                      onChange={(event) => {
                        setName(event.target.value);
                        setNameError(event.target.value === null);

                        fillDetails(
                          requestor.Name,
                          event.target.value,
                          language.Name,
                          interpretationType,
                        );
                      }}
                      sx={{
                        ...getTextFieldBorderStyles(
                          getBorderColor(
                            theme,
                            isNewAssignment,
                            name || "",
                            overlayData.Name,
                          ),
                        ),
                        ...getShakeSx("Name"),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      select
                      id={"InterpretationType"}
                      label="Interpretation Type"
                      error={interpretationTypeError}
                      value={interpretationType}
                      onChange={(event) => {
                        setInterpretationType(event.target.value);
                        setInterpretationTypeError(event.target.value === null);

                        fillDetails(
                          requestor.Name,
                          name,
                          language.Name,
                          event.target.value,
                        );
                      }}
                      sx={{
                        ...getTextFieldBorderStyles(
                          getBorderColor(
                            theme,
                            isNewAssignment,
                            interpretationType || "",
                            overlayData.InterpretationType,
                          ),
                        ),
                        ...getShakeSx("InterpretationType"),
                      }}
                    >
                      {overlayData.InterpretationTypes.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              ) : null}

              {!completing ? (
                <Grid container size={{ xs: 12, md: 6 }}>
                  <Grid size={{ xs: 12 }}>
                    <Autocomplete
                      disabled={!isEditable}
                      disablePortal
                      disableClearable
                      key={"Client"}
                      size="small"
                      options={overlayData.Clients}
                      getOptionLabel={(client) => client?.Account || ""}
                      value={client}
                      onChange={(event, newValue) => {
                        let clientRate = 0;

                        if (typeof newValue === "string") {
                          setClient({
                            Account: newValue,
                          });
                          setAddNewClient(false);
                        } else if (newValue && newValue.inputValue) {
                          // Create a new value from the user input
                          setClient({
                            Account: newValue.inputValue,
                          });

                          setAddNewClient(true);
                        } else {
                          setClient(newValue);
                          setAddNewClient(false);

                          if (language)
                            clientRate = updateClientRate(newValue, language);
                          setClientRate(clientRate);
                          setClientRateSimultaneous(clientRate * 1.5);
                        }

                        setRequestor("");

                        setClientError(newValue === null);
                      }}
                      filterOptions={(options, params) => {
                        const { inputValue } = params;

                        // Filter by both Account and ShortName
                        const filtered = options.filter((option) => {
                          const accountMatch =
                            option.Account?.toLowerCase().includes(
                              inputValue.toLowerCase(),
                            );
                          const shortNameMatch =
                            option.ShortName?.toLowerCase().includes(
                              inputValue.toLowerCase(),
                            );
                          return accountMatch || shortNameMatch;
                        });

                        // Suggest the creation of a new value
                        const isExisting = options.some(
                          (option) =>
                            inputValue === option.Account ||
                            inputValue === option.ShortName,
                        );
                        if (inputValue !== "" && !isExisting) {
                          filtered.push({
                            inputValue,
                            Account: `Add "${inputValue}"`,
                          });
                        }

                        return filtered;
                      }}
                      selectOnFocus
                      clearOnBlur
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Client"
                          placeholder=""
                          error={clientError}
                        />
                      )}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (client && client.Account) || "",
                              (overlayData.Client &&
                                overlayData.Client.Account) ||
                                "",
                            ),
                          },
                        },
                        ...getShakeSx("Client"),
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  select
                  id={"Mode"}
                  label="Mode"
                  error={modeError}
                  value={mode}
                  onChange={(event) => {
                    setMode(event.target.value);
                    setModeError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          mode || "",
                          overlayData.Mode,
                          true,
                        ),
                      },
                    },
                  }}
                >
                  {["Consecutive", "Simultaneous"].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {!completing ? (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    disabled={!isEditable}
                    disablePortal
                    disableClearable
                    key={"Language"}
                    size="small"
                    options={overlayData.Languages}
                    getOptionLabel={(language) => language?.Name || ""}
                    value={language}
                    onChange={(event, newValue) => {
                      if (typeof newValue === "string") {
                        setLanguage({
                          Name: newValue,
                        });
                        fillDetails(
                          requestor.Name,
                          name,
                          newValue,
                          interpretationType,
                        );
                      } else if (newValue && newValue.inputValue) {
                        // Create a new value from the user input
                        setLanguage({
                          Name: newValue.inputValue,
                        });
                        fillDetails(
                          requestor.Name,
                          name,
                          newValue.inputValue,
                          interpretationType,
                        );
                      } else {
                        setLanguage(newValue);
                        fillDetails(
                          requestor.Name,
                          name,
                          newValue.Name,
                          interpretationType,
                        );

                        if (client) {
                          const tempRate = updateClientRate(
                            client,
                            newValue.id,
                          );

                          setClientRate(tempRate);
                          setClientRateSimultaneous(tempRate * 1.5);
                        }
                      }

                      setLinguist("");
                      setRate(0);
                      setRateSimultaneous(0);
                      setLanguageError(newValue === null);
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);

                      const { inputValue } = params;
                      // Suggest the creation of a new value
                      const isExisting = options.some(
                        (option) => inputValue === option.Name,
                      );
                      if (inputValue !== "" && !isExisting) {
                        filtered.push({
                          inputValue,
                          Name: `Add "${inputValue}"`,
                        });
                      }

                      return filtered;
                    }}
                    selectOnFocus
                    clearOnBlur
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Language"
                        placeholder=""
                        error={languageError}
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            (language && language?.Name) || "",
                            (overlayData &&
                              overlayData.ServiceTargetLanguages &&
                              overlayData.ServiceTargetLanguages[0].Language
                                ?.Name) ||
                              "",
                          ),
                        },
                      },
                      ...getShakeSx("Language"),
                    }}
                  />
                </Grid>
              ) : null}

              <Grid
                size={{
                  xs: 12,
                  md:
                    completing || isNewAssignment
                      ? mode === "Simultaneous"
                        ? completing
                          ? 2
                          : 3
                        : completing
                          ? 3
                          : 6
                      : mode === "Simultaneous"
                        ? 2
                        : 3,
                }}
              >
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"Client Rate"}
                  label="Client Rate"
                  error={clientRateError}
                  value={clientRate}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  onChange={(event) => {
                    setClientRate(event.target.value);
                    setClientRateError(event.target.value === null);
                    setClientRateSimultaneous(event.target.value * 1.5);
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(clientRate) || "",
                          Number(initialClientRate),
                        ),
                      },
                    },
                    ...getShakeSx("ClientRate"),
                  }}
                />
              </Grid>

              {mode === "Simultaneous" ? (
                <Grid size={{ xs: 12, md: isNewAssignment ? 3 : 2 }}>
                  <TextField
                    disabled={!isEditable}
                    size="small"
                    id={"Client Rate Simultaneous"}
                    label="Client Rate (Simultaneous)"
                    error={clientRateSimultaneousError}
                    value={clientRateSimultaneous}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      },
                    }}
                    onChange={(event) => {
                      setClientRateSimultaneous(event.target.value);
                      setClientRateSimultaneousError(
                        event.target.value === null,
                      );
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            Number(clientRateSimultaneous) || "",
                            Number(overlayData.ServiceClientRateSimultaneous) ||
                              "",
                            true,
                          ),
                        },
                      },
                      ...getShakeSx("ClientRateSimultaneous"),
                    }}
                  />
                </Grid>
              ) : null}

              {completing || !isNewAssignment ? (
                <Grid size={{ xs: 12, md: mode === "Simultaneous" ? 2 : 3 }}>
                  <TextField
                    disabled={!isEditable}
                    size="small"
                    id={"Client Rate Discount"}
                    label="Client Rate Discount"
                    error={clientRateDiscountError}
                    value={clientRateDiscount}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">%</InputAdornment>
                        ),
                      },
                    }}
                    onChange={(event) => {
                      setClientRateDiscount(event.target.value);
                      setClientRateDiscountError(event.target.value === null);
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            Number(clientRateDiscount) || "",
                            Number(overlayData.ClientRateDiscount),
                            true,
                          ),
                        },
                      },
                    }}
                  />
                </Grid>
              ) : null}
              {!completing ? (
                <Grid container size={{ xs: 12 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Autocomplete
                      disabled={!isEditable}
                      disablePortal
                      disableClearable
                      key={"Requestor"}
                      size="small"
                      id={"Requestor"}
                      options={(client && client.Requestors) || []}
                      getOptionLabel={(requestor) => requestor?.Name || ""}
                      value={requestor}
                      onChange={(event, newValue) => {
                        if (typeof newValue === "string") {
                          setRequestor({
                            Name: newValue,
                          });
                          setAddNewRequestor(false);
                          fillDetails(
                            newValue,
                            name,
                            language.Name,
                            interpretationType,
                          );
                        } else if (newValue && newValue.inputValue) {
                          // Create a new value from the user input
                          setRequestor({
                            Name: newValue.inputValue,
                          });
                          setAddNewRequestor(true);
                          fillDetails(
                            newValue.inputValue,
                            name,
                            language.Name,
                            interpretationType,
                          );
                        } else {
                          setRequestor(newValue);
                          setAddNewRequestor(false);
                          fillDetails(
                            newValue.Name,
                            name,
                            language.Name,
                            interpretationType,
                          );
                        }

                        setRequestorError(newValue === null);
                      }}
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);

                        const { inputValue } = params;
                        // Suggest the creation of a new value
                        const isExisting = options.some(
                          (option) => inputValue === option.Name,
                        );
                        if (inputValue !== "" && !isExisting) {
                          filtered.push({
                            inputValue,
                            Name: `Add "${inputValue}"`,
                          });
                        }

                        return filtered;
                      }}
                      selectOnFocus
                      clearOnBlur
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Requestor"
                          placeholder=""
                          error={requestorError}
                        />
                      )}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (requestor && requestor.Name) || "",
                              (overlayData &&
                                overlayData.Requestor &&
                                overlayData.Requestor.Name) ||
                                "",
                            ),
                          },
                        },
                        ...getShakeSx("Requestor"),
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Autocomplete
                      disabled={!isEditable}
                      disablePortal
                      disableClearable
                      key={"InvoiceTo"}
                      size="small"
                      id={"InvoiceTo"}
                      options={(client && client.Requestors) || []}
                      getOptionLabel={(requestor) => requestor?.Name || ""}
                      value={invoiceTo}
                      onChange={(event, newValue) => {
                        if (typeof newValue === "string") {
                          setInvoiceTo({
                            Name: newValue,
                          });
                          setAddNewInvoiceTo(false);
                        } else if (newValue && newValue.inputValue) {
                          // Create a new value from the user input
                          setInvoiceTo({
                            Name: newValue.inputValue,
                          });
                          setAddNewInvoiceTo(true);
                        } else {
                          setInvoiceTo(newValue);
                          setAddNewInvoiceTo(false);
                        }

                        setInvoiceToError(newValue === null);
                      }}
                      filterOptions={(options, params) => {
                        const filtered = filter(options, params);

                        const { inputValue } = params;
                        // Suggest the creation of a new value
                        const isExisting = options.some(
                          (option) => inputValue === option.Name,
                        );
                        if (inputValue !== "" && !isExisting) {
                          filtered.push({
                            inputValue,
                            Name: `Add "${inputValue}"`,
                          });
                        }

                        return filtered;
                      }}
                      selectOnFocus
                      clearOnBlur
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Invoice To"
                          placeholder=""
                          error={invoiceToError}
                        />
                      )}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (invoiceTo && invoiceTo?.Name) || "",
                              (overlayData.Invoicer &&
                                overlayData.Invoicer?.Name) ||
                                "",
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  disabled={!isEditable}
                  disablePortal
                  disableClearable
                  key={"Linguist"}
                  size="small"
                  options={filterLinguistsByLanguage(
                    overlayData.Linguists,
                    language?.Name,
                    showAllLinguists,
                  )}
                  getOptionLabel={(linguist) => linguist?.Name || ""}
                  value={linguist}
                  onChange={(event, newValue) => {
                    if (typeof newValue === "string") {
                      setLinguist({ Name: newValue });
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      setLinguist({ Name: newValue.inputValue });
                    } else {
                      setLinguist(newValue);
                    }

                    setLinguistError(newValue === null);

                    if (newValue?.Name === "N/A") {
                      handleClear();
                    }

                    if (
                      newValue &&
                      newValue.LinguistLanguages &&
                      newValue.LinguistLanguages.length > 0
                    ) {
                      const linguistCurrentRateRaw =
                        newValue.LinguistLanguages[0].Rate;

                      //TO DO FETCH THE SIMULTANEOUS RATE HERE ALSO DEPENDING ON THE LINGUIST'S RATE
                      const assignmentRateRaw = overlayData.Rate;

                      const linguistCurrentRateNum = Number(
                        linguistCurrentRateRaw,
                      );
                      const assignmentRateNum = Number(assignmentRateRaw);

                      const linguistCurrentRate = Number.isNaN(
                        linguistCurrentRateNum,
                      )
                        ? 0
                        : linguistCurrentRateNum;
                      const assignmentRate = Number.isNaN(assignmentRateNum)
                        ? 0
                        : assignmentRateNum;

                      // If linguist unchanged and rates differ, keep assignment rate and show helper
                      if (
                        !isNewAssignment &&
                        newValue?.id === originalLinguistId &&
                        assignmentRate !== linguistCurrentRate
                      ) {
                        setRateHelperText(
                          `Linguist New Rate: $${linguistCurrentRate} | Linguist Assignment Rate: $${assignmentRate}`,
                        );
                        setRate(assignmentRate);
                        setRateSimultaneous(assignmentRate * 1.5);
                      } else {
                        // If linguist changed, use their current rate and clear helper
                        setRate(linguistCurrentRate);
                        setRateSimultaneous(linguistCurrentRate * 1.5);
                        setRateHelperText("");
                      }

                      setTwoHoursMin(
                        newValue.MinimumHours && newValue.MinimumHours === 2,
                      );

                      setIsSavingChanges(false);
                    } else {
                      setRate("");
                      setRateSimultaneous("");
                      setRateHelperText("");
                    }
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);
                    const { inputValue } = params;

                    const isExisting = options.some(
                      (option) => inputValue === option.Name,
                    );
                    if (inputValue !== "" && !isExisting) {
                      filtered.push({
                        inputValue,
                        Name: `Add "${inputValue}"`,
                      });
                    }

                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Linguist"
                      placeholder=""
                      error={linguistError}
                      helperText={(() => {
                        const notes = linguist?.Notes;
                        if (typeof notes === "string" && notes.includes("$")) {
                          return notes;
                        }
                        return null;
                      })()}
                      // yes, InputProps is marked deprecated in types, but this is still
                      // the supported Autocomplete pattern for now
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {linguist && linguist.Name !== "N/A" && (
                              <IconButton
                                onClick={handleClear}
                                edge="end"
                                tabIndex={-1}
                                size="small"
                                sx={{
                                  p: 0.25, // smaller padding so it doesn't increase height
                                  "& .MuiSvgIcon-root": {
                                    fontSize: 16, // smaller icon
                                  },
                                }}
                              >
                                <ClearRoundedIcon />
                              </IconButton>
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{
                    ...getShakeSx("Linguist"),
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          linguist?.Name || "",
                          (overlayData.Linguist && overlayData.Linguist.Name) ||
                            "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {/* First row: Rate and 2 Hr Min checkbox */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {/* Rate and RateSimultaneous: split 65% width */}
                    <Box
                      sx={{
                        flex: "1 1 0%",
                        minWidth: "65%",
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flex: "1 1 50%" }}>
                        <TextField
                          disabled={!isEditable}
                          size="small"
                          id={"Rate"}
                          fullWidth
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            },
                          }}
                          label="Rate"
                          error={rateError}
                          value={rate}
                          helperText={rateHelperText}
                          onChange={(event) => {
                            setRate(event.target.value);
                            setRateError(event.target.value === null);
                            setRateHelperText(""); // clear helper when manually edited
                            setRateSimultaneous(event.target.value * 1.5);
                          }}
                          sx={{
                            "& .MuiInputBase-input": {
                              padding: "8.5px 12px !important",
                            },
                            "& .MuiOutlinedInput-root": {
                              py: 0,
                              "&.MuiInputBase-root fieldset": {
                                borderColor: getBorderColor(
                                  theme,
                                  isNewAssignment,
                                  Number(rate) || "",
                                  Number(overlayData.Rate),
                                ),
                              },
                            },
                          }}
                        />
                      </Box>

                      {mode === "Simultaneous" && (
                        <Box sx={{ flex: "1 1 50%" }}>
                          <TextField
                            disabled={!isEditable}
                            size="small"
                            id={"RateSimultaneous"}
                            label="Rate (Simultaneous)"
                            error={rateSimultaneousError}
                            value={rateSimultaneous}
                            fullWidth
                            slotProps={{
                              input: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    $
                                  </InputAdornment>
                                ),
                              },
                            }}
                            onChange={(event) => {
                              setRateSimultaneous(event.target.value);
                              setRateSimultaneousError(
                                event.target.value === null,
                              );
                            }}
                            sx={{
                              "& .MuiInputBase-input": {
                                padding: "8.5px 12px !important",
                              },
                              "& .MuiOutlinedInput-root": {
                                py: 0,
                                "&.MuiInputBase-root fieldset": {
                                  borderColor: getBorderColor(
                                    theme,
                                    isNewAssignment,
                                    Number(rateSimultaneous) || "",
                                    Number(overlayData.RateSimultaneous) || "",
                                    true,
                                  ),
                                },
                              },
                              ...getShakeSx("RateSimultaneous"),
                            }}
                          />
                        </Box>
                      )}
                    </Box>

                    {/* Tooltip + checkbox: can shrink, max ~35% */}
                    <Box
                      sx={{
                        px: 0.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap", // optional, if you want them to wrap on small screens
                      }}
                    >
                      <HtmlTooltip
                        title={
                          <Box p=".5rem">
                            <Typography
                              color="inherit"
                              style={{ whiteSpace: "pre-line" }}
                            >
                              {
                                "Will the client be charged, and the linguist be paid at a 2 hours minimum for this assignment?"
                              }
                            </Typography>
                          </Box>
                        }
                      >
                        <FormControlLabel
                          control={<Checkbox />}
                          id={"TwoHoursMinimum"}
                          label={"2 Hr Min"}
                          checked={twoHoursMin}
                          onChange={(event) => {
                            setTwoHoursMin(event.target.checked);
                          }}
                        />
                      </HtmlTooltip>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {!completing ? (
                <Grid container size={{ xs: 12, md: 6 }}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      id={"contactPersonName"}
                      label="Contact Person Name"
                      value={contactPersonName}
                      onChange={(event) => {
                        setContactPersonName(event.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              contactPersonName || "",
                              overlayData.ContactPersonName,
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}

              {!completing ? (
                <Grid container size={{ xs: 12, md: 6 }}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      id={"contactPersonNumber"}
                      label="Contact Person Number"
                      value={contactPersonNumber}
                      onChange={(event) => {
                        setContactPersonNumber(event.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              contactPersonNumber || "",
                              overlayData.ContactPersonNumber,
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid container size={{ xs: 12, md: 6 }}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    disabled={!isEditable}
                    size="small"
                    id={"Address"}
                    label="Address"
                    error={addressError}
                    value={address}
                    onChange={(event) => {
                      setAddress(event.target.value);
                      setAddressError(event.target.value === null);
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor: getBorderColor(
                            theme,
                            isNewAssignment,
                            address || "",
                            overlayData.Address,
                          ),
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  select
                  id={"InterpretationStatus"}
                  label="Interpretation Status"
                  error={interpretationStatusError}
                  //defaultValue={interpretationStatus === null ? "Requested" : ""}
                  value={interpretationStatus}
                  onChange={(event) => {
                    setInterpretationStatus(event.target.value);
                    setInterpretationStatusError(event.target.value === null);

                    if (!event.target.value.startsWith("Cancel"))
                      setCancellationReason("");
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          interpretationStatus || "",
                          overlayData.InterpretationStatus,
                        ),
                      },
                    },
                  }}
                >
                  {overlayData.InterpretationStatuses.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {!completing && interpretationStatus?.startsWith("Cancel") ? (
                <Grid container size={{ xs: 12 }}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      id={"CancellationReason"}
                      label="Cancellation Reason"
                      value={cancellationReason}
                      onChange={(event) => {
                        setCancellationReason(event.target.value);
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              cancellationReason || "",
                              overlayData.CancellationReason,
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid size={{ xs: 12, md: 4 }}>
                <MobileTimePicker
                  disabled={completing ? true : !isEditable}
                  value={timeStart || null}
                  label="Start Time"
                  onChange={(newTime) => {
                    setTimeStart(newTime);
                    setTimeStartError(!newTime);
                    setActualStart(newTime);
                    setActualStartError(!newTime);

                    // If start time is 11AM or later, automatically set end time to 1 hour later
                    let effectiveTimeEnd = timeEnd;
                    if (newTime) {
                      const autoEndTime = newTime.add(1, "hour");
                      setTimeEnd(autoEndTime);
                      setTimeEndError(false);
                      setActualEnd(autoEndTime);
                      setActualEndError(false);
                      effectiveTimeEnd = autoEndTime;
                    }

                    if (newTime && effectiveTimeEnd) {
                      const tempDuration = getDurationValue(
                        newTime,
                        effectiveTimeEnd,
                      );

                      setDuration(tempDuration);
                      setActualDuration(tempDuration);

                      const tempTotalDuration = sumDurationValues([
                        tempDuration || 0,
                        schedulingTime,
                        preparationTime,
                      ]);

                      setTotalDuration(tempTotalDuration);
                    }
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: timeStartError,
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColorTimes(
                          (timeStart && timeStart.format("HH:mm:ss")) || "",
                          overlayData.TimeStart || "",
                          (actualStart && actualStart.format("HH:mm:ss")) || "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <MobileTimePicker
                  disabled={completing ? true : !isEditable}
                  value={timeEnd || null}
                  label="End Time"
                  onChange={(newTime) => {
                    setTimeEnd(newTime);
                    setTimeEndError(!newTime);
                    setActualEnd(newTime);
                    setActualEndError(!newTime);

                    if (timeStart && newTime) {
                      const tempDuration = getDurationValue(timeStart, newTime);

                      setDuration(tempDuration);
                      setActualDuration(tempDuration);

                      const tempTotalDuration = sumDurationValues([
                        tempDuration || 0,
                        schedulingTime,
                        preparationTime,
                      ]);

                      setTotalDuration(tempTotalDuration);
                    }
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: timeEndError,
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColorTimes(
                          (timeEnd && timeEnd.format("HH:mm:ss")) || "",
                          overlayData.TimeEnd || "",
                          (actualEnd && actualEnd.format("HH:mm:ss")) || "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TimeField
                  disabled={false}
                  readOnly
                  label="Duration"
                  value={duration}
                  format="HH:mm"
                  size="small"
                  id={"Duration"}
                  slotProps={{
                    textField: {
                      InputProps: createTimeFieldAdornments(
                        theme,
                        !isEditable || !timeStart,
                        () => {
                          if (duration && timeStart) {
                            // Decrement duration by 15 minutes
                            const currentMinutes =
                              duration.hour() * 60 + duration.minute();
                            const newMinutes = Math.max(0, currentMinutes - 15);
                            const newDuration = dayjs()
                              .hour(Math.floor(newMinutes / 60))
                              .minute(newMinutes % 60)
                              .second(0);

                            // Update end time based on new duration
                            const newTimeEnd = timeStart.add(
                              newMinutes,
                              "minute",
                            );

                            setDuration(newDuration);
                            setActualDuration(newDuration);
                            setTimeEnd(newTimeEnd);
                            setActualEnd(newTimeEnd);
                            setTimeEndError(!newTimeEnd);
                            setActualEndError(!newTimeEnd);

                            const tempTotalDuration = sumDurationValues([
                              newDuration
                                ? newDuration.hour() * 60 + newDuration.minute()
                                : 0,
                              schedulingTime,
                              preparationTime,
                            ]);

                            setTotalDuration(tempTotalDuration);
                          }
                        },
                        () => {
                          if (duration && timeStart) {
                            // Increment duration by 15 minutes
                            const currentMinutes =
                              duration.hour() * 60 + duration.minute();
                            const newMinutes = currentMinutes + 15;
                            const newDuration = dayjs()
                              .hour(Math.floor(newMinutes / 60))
                              .minute(newMinutes % 60)
                              .second(0);

                            // Update end time based on new duration
                            const newTimeEnd = timeStart.add(
                              newMinutes,
                              "minute",
                            );

                            setDuration(newDuration);
                            setActualDuration(newDuration);
                            setTimeEnd(newTimeEnd);
                            setActualEnd(newTimeEnd);
                            setTimeEndError(!newTimeEnd);
                            setActualEndError(!newTimeEnd);

                            const tempTotalDuration = sumDurationValues([
                              newDuration
                                ? newDuration.hour() * 60 + newDuration.minute()
                                : 0,
                              schedulingTime,
                              preparationTime,
                            ]);

                            setTotalDuration(tempTotalDuration);
                          }
                        },
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                      cursor: "default",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          (duration && duration.format("HH:mm:ss")) || "",
                          overlayData.Duration || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              {showActualTimes || completing ? (
                <Grid container>
                  <Grid size={{ xs: 12 }}>
                    <Divider />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <MobileTimePicker
                      disabled={!isEditable}
                      value={actualStart || null}
                      label="Actual Start"
                      onChange={(newTime) => {
                        setActualStart(newTime);
                        setActualStartError(!newTime);

                        const tempActualDuration = getDurationValue(
                          newTime,
                          actualEnd,
                        );

                        setActualDuration(tempActualDuration);

                        const tempTotalDuration = sumDurationValues([
                          tempActualDuration && duration
                            ? Math.max(
                                tempActualDuration.hour() * 60 +
                                  tempActualDuration.minute(),
                                duration.hour() * 60 + duration.minute(),
                              )
                            : tempActualDuration || duration || 0,
                          schedulingTime,
                          preparationTime,
                        ]);

                        setTotalDuration(tempTotalDuration);
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: actualStartError,
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (actualStart && actualStart.format("HH:mm:ss")) ||
                                "",
                              overlayData.ActualStart || "",
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <MobileTimePicker
                      disabled={!isEditable}
                      value={actualEnd || null}
                      label="Actual End"
                      onChange={(newTime) => {
                        setActualEnd(newTime);
                        setActualEndError(!newTime);

                        const tempActualDuration = getDurationValue(
                          actualStart,
                          newTime,
                        );

                        setActualDuration(tempActualDuration);

                        const tempTotalDuration = sumDurationValues([
                          tempActualDuration && duration
                            ? Math.max(
                                tempActualDuration.hour() * 60 +
                                  tempActualDuration.minute(),
                                duration.hour() * 60 + duration.minute(),
                              )
                            : tempActualDuration || duration || 0,
                          schedulingTime,
                          preparationTime,
                        ]);

                        setTotalDuration(tempTotalDuration);
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: actualEndError,
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (actualEnd && actualEnd.format("HH:mm:ss")) || "",
                              overlayData.ActualEnd || "",
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TimeField
                      disabled={false}
                      readOnly
                      label="Actual Duration"
                      value={actualDuration}
                      format="HH:mm"
                      size="small"
                      id={"ActualDuration"}
                      slotProps={{
                        textField: {
                          InputProps: createTimeFieldAdornments(
                            theme,
                            !isEditable || !actualStart,
                            () => {
                              if (actualDuration && actualStart) {
                                // Decrement actual duration by 15 minutes
                                const currentMinutes =
                                  actualDuration.hour() * 60 +
                                  actualDuration.minute();
                                const newMinutes = Math.max(
                                  0,
                                  currentMinutes - 15,
                                );
                                const newActualDuration = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                // Update actual end time based on new duration
                                const newActualEnd = actualStart.add(
                                  newMinutes,
                                  "minute",
                                );

                                setActualDuration(newActualDuration);
                                setActualEnd(newActualEnd);
                                setActualEndError(!newActualEnd);

                                const tempTotalDuration = sumDurationValues([
                                  newActualDuration && duration
                                    ? Math.max(
                                        newActualDuration.hour() * 60 +
                                          newActualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : newActualDuration || duration || 0,
                                  schedulingTime,
                                  preparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);
                              }
                            },
                            () => {
                              if (actualDuration && actualStart) {
                                // Increment actual duration by 15 minutes
                                const currentMinutes =
                                  actualDuration.hour() * 60 +
                                  actualDuration.minute();
                                const newMinutes = currentMinutes + 15;
                                const newActualDuration = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                // Update actual end time based on new duration
                                const newActualEnd = actualStart.add(
                                  newMinutes,
                                  "minute",
                                );

                                setActualDuration(newActualDuration);
                                setActualEnd(newActualEnd);
                                setActualEndError(!newActualEnd);

                                const tempTotalDuration = sumDurationValues([
                                  newActualDuration && duration
                                    ? Math.max(
                                        newActualDuration.hour() * 60 +
                                          newActualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : newActualDuration || duration || 0,
                                  schedulingTime,
                                  preparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);
                              }
                            },
                          ),
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                          cursor: "default",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (actualDuration &&
                                actualDuration.format("HH:mm:ss")) ||
                                "",
                              overlayData.ActualDuration || "",
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TimeField
                      disabled={false}
                      readOnly
                      label="Scheduling Time"
                      value={schedulingTime}
                      format="HH:mm"
                      size="small"
                      id={"SchedulingTime"}
                      slotProps={{
                        textField: {
                          error: schedulingTimeError,
                          InputProps: createTimeFieldAdornments(
                            theme,
                            !isEditable,
                            () => {
                              if (schedulingTime) {
                                // Decrement scheduling time by 15 minutes
                                const currentMinutes =
                                  schedulingTime.hour() * 60 +
                                  schedulingTime.minute();
                                const newMinutes = Math.max(
                                  0,
                                  currentMinutes - 15,
                                );
                                const newSchedulingTime = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                setSchedulingTime(newSchedulingTime);
                                setSchedulingTimeError(!newSchedulingTime);

                                const tempTotalDuration = sumDurationValues([
                                  actualDuration && duration
                                    ? Math.max(
                                        actualDuration.hour() * 60 +
                                          actualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : actualDuration || duration || 0,
                                  newSchedulingTime,
                                  preparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);

                                // Reset manual flags for charges when times change
                              }
                            },
                            () => {
                              if (schedulingTime) {
                                // Increment scheduling time by 15 minutes
                                const currentMinutes =
                                  schedulingTime.hour() * 60 +
                                  schedulingTime.minute();
                                const newMinutes = currentMinutes + 15;
                                const newSchedulingTime = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                setSchedulingTime(newSchedulingTime);
                                setSchedulingTimeError(!newSchedulingTime);

                                const tempTotalDuration = sumDurationValues([
                                  actualDuration && duration
                                    ? Math.max(
                                        actualDuration.hour() * 60 +
                                          actualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : actualDuration || duration || 0,
                                  newSchedulingTime,
                                  preparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);
                              } else {
                                // Initialize with 15 minutes if null
                                const newSchedulingTime = dayjs()
                                  .hour(0)
                                  .minute(15)
                                  .second(0);

                                setSchedulingTime(newSchedulingTime);
                                setSchedulingTimeError(!newSchedulingTime);

                                const tempTotalDuration = sumDurationValues([
                                  actualDuration && duration
                                    ? Math.max(
                                        actualDuration.hour() * 60 +
                                          actualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : actualDuration || duration || 0,
                                  newSchedulingTime,
                                  preparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);
                              }
                            },
                          ),
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                          cursor: "default",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (schedulingTime &&
                                schedulingTime.format("HH:mm:ss")) ||
                                "",
                              overlayData.SchedulingTime || "",
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TimeField
                      disabled={false}
                      readOnly
                      label="Preparation Time"
                      value={preparationTime}
                      format="HH:mm"
                      size="small"
                      id={"PreparationTime"}
                      slotProps={{
                        textField: {
                          InputProps: createTimeFieldAdornments(
                            theme,
                            !isEditable,
                            () => {
                              if (preparationTime) {
                                // Decrement preparation time by 15 minutes
                                const currentMinutes =
                                  preparationTime.hour() * 60 +
                                  preparationTime.minute();
                                const newMinutes = Math.max(
                                  0,
                                  currentMinutes - 15,
                                );
                                const newPreparationTime = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                setPreparationTime(newPreparationTime);

                                const tempTotalDuration = sumDurationValues([
                                  actualDuration && duration
                                    ? Math.max(
                                        actualDuration.hour() * 60 +
                                          actualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : actualDuration || duration || 0,
                                  schedulingTime,
                                  newPreparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);
                              }
                            },
                            () => {
                              if (preparationTime) {
                                // Increment preparation time by 15 minutes
                                const currentMinutes =
                                  preparationTime.hour() * 60 +
                                  preparationTime.minute();
                                const newMinutes = currentMinutes + 15;
                                const newPreparationTime = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                setPreparationTime(newPreparationTime);

                                const tempTotalDuration = sumDurationValues([
                                  actualDuration && duration
                                    ? Math.max(
                                        actualDuration.hour() * 60 +
                                          actualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : actualDuration || duration || 0,
                                  schedulingTime,
                                  newPreparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);
                              } else {
                                // Initialize with 15 minutes if null
                                const newPreparationTime = dayjs()
                                  .hour(0)
                                  .minute(15)
                                  .second(0);

                                setPreparationTime(newPreparationTime);

                                const tempTotalDuration = sumDurationValues([
                                  actualDuration && duration
                                    ? Math.max(
                                        actualDuration.hour() * 60 +
                                          actualDuration.minute(),
                                        duration.hour() * 60 +
                                          duration.minute(),
                                      )
                                    : actualDuration || duration || 0,
                                  schedulingTime,
                                  newPreparationTime,
                                ]);

                                setTotalDuration(tempTotalDuration);
                              }
                            },
                          ),
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                          cursor: "default",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (preparationTime &&
                                preparationTime.format("HH:mm:ss")) ||
                                "",
                              overlayData.PreparationTime || "",
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TimeField
                      disabled={false}
                      readOnly
                      label="Total Duration"
                      value={totalDuration}
                      format="HH:mm"
                      size="small"
                      id={"TotalDuration"}
                      slotProps={{
                        textField: {
                          InputProps: createTimeFieldAdornments(
                            theme,
                            !allowTotalDurationEditing,
                            () => {
                              if (totalDuration) {
                                // Decrement total duration by 15 minutes
                                const currentMinutes =
                                  totalDuration.hour() * 60 +
                                  totalDuration.minute();
                                const newMinutes = Math.max(
                                  0,
                                  currentMinutes - 15,
                                );
                                const newTotalDuration = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                setTotalDuration(newTotalDuration);

                                // Reset manual flags for charges when times change
                              }
                            },
                            () => {
                              if (totalDuration) {
                                // Increment total duration by 15 minutes
                                const currentMinutes =
                                  totalDuration.hour() * 60 +
                                  totalDuration.minute();
                                const newMinutes = currentMinutes + 15;
                                const newTotalDuration = dayjs()
                                  .hour(Math.floor(newMinutes / 60))
                                  .minute(newMinutes % 60)
                                  .second(0);

                                setTotalDuration(newTotalDuration);

                                // Reset manual flags for charges when times change
                              } else {
                                // Initialize with 15 minutes if null
                                const newTotalDuration = dayjs()
                                  .hour(0)
                                  .minute(15)
                                  .second(0);

                                setTotalDuration(newTotalDuration);

                                // Reset manual flags for charges when times change
                              }
                            },
                          ),
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                          cursor: "default",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              (totalDuration &&
                                totalDuration.format("HH:mm:ss")) ||
                                "",
                              overlayData.TotalDuration || "",
                              true,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Divider />
                  </Grid>
                </Grid>
              ) : null}

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"MileageRoundtrip"}
                  label="Mileage Roundtrip"
                  error={mileageRoundtripError}
                  value={mileageRoundtrip}
                  onFocus={() => {
                    if (Number(mileageRoundtrip) === 0) {
                      setMileageRoundtrip("");
                      //setMileageCost("");
                    }
                  }}
                  onBlur={() => {
                    if (Number(mileageRoundtrip) === 0) {
                      setMileageRoundtrip(0);
                      //setMileageCost("");
                    }
                  }}
                  onChange={(event) => {
                    setMileageRoundtrip(event.target.value);
                    setMileageRoundtripError(event.target.value === null);
                    const year = date ? date.year() : dayjs.tz().year();
                    const mileageRate = getMileageRateForYear(year);
                    setMileageCost(
                      event.target.value
                        ? (event.target.value * mileageRate).toFixed(2)
                        : "",
                    );
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(mileageRoundtrip) || "",
                          Number(overlayData.MileageRoundtrip) || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"MileageCost"}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  label="Mileage Cost"
                  error={mileageCostError}
                  value={mileageCost}
                  onFocus={() => {
                    if (Number(mileageCost) === 0) {
                      setMileageCost("");
                    }
                  }}
                  onBlur={() => {
                    if (Number(mileageCost) === 0) {
                      setMileageCost(0);
                    }
                  }}
                  onChange={(event) => {
                    setMileageCost(event.target.value);
                    setMileageCostError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(mileageCost) || "",
                          Number(overlayData.MileageCost) || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"ParkingFeesTolls"}
                  label="Parking Fees Tolls"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  error={parkingFeesTollsError}
                  value={parkingFeesTolls}
                  onFocus={() => {
                    if (Number(parkingFeesTolls) === 0) {
                      setParkingFeesTolls("");
                    }
                  }}
                  onBlur={() => {
                    if (Number(parkingFeesTolls) === 0) {
                      setParkingFeesTolls(0);
                    }
                  }}
                  onChange={(event) => {
                    setParkingFeesTolls(event.target.value);
                    setParkingFeesTollsError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(parkingFeesTolls) || "",
                          Number(overlayData.ParkingFeesTolls) || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"LinguistPay"}
                  slotProps={{
                    input: {
                      readOnly: !disableAutomaticCalculationOfCharges,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  label="Linguist Pay"
                  error={linguistPayError}
                  value={linguistPay}
                  onFocus={() => {
                    if (Number(linguistPay) === 0) {
                      setLinguistPay("");
                    }
                  }}
                  onBlur={() => {
                    if (Number(linguistPay) === 0) {
                      setLinguistPay(0);
                    }
                  }}
                  onChange={(event) => {
                    setLinguistPay(event.target.value);
                    setLinguistPayError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(linguistPay) || "",
                          Number(overlayData.LinguistPay) || "",
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"AdditionalLinguistCost"}
                  label="Additional Linguist Cost"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  error={additionalLinguistCostError}
                  value={additionalLinguistCost}
                  onFocus={() => {
                    if (Number(additionalLinguistCost) === 0) {
                      setAdditionalLinguistCost("");
                    }
                  }}
                  onBlur={() => {
                    if (Number(additionalLinguistCost) === 0) {
                      setAdditionalLinguistCost(0);
                    }
                  }}
                  onChange={(event) => {
                    setAdditionalLinguistCost(event.target.value);
                    setAdditionalLinguistCostError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(additionalLinguistCost) || "",
                          Number(overlayData.AdditionalLinguistCost) || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid container size={{ xs: 12 }}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    disabled={!isEditable}
                    size="small"
                    id={"TotalLinguistPay"}
                    label="Total Linguist Pay"
                    helperText={totalLinguistPayHelperText}
                    slotProps={{
                      input: {
                        readOnly: !disableAutomaticCalculationOfCharges,
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      },
                    }}
                    value={totalLinguistPay}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setTotalLinguistPay(nextValue);

                      if (disableAutomaticCalculationOfCharges) {
                        setLinguistPay(nextValue);
                      }
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor:
                            totalLinguistPay === null || totalLinguistPay === ""
                              ? theme.palette.grey.main
                              : theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"AdditionalCost"}
                  label="Additional Cost"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  error={additionalCostError}
                  value={additionalCost}
                  onFocus={() => {
                    if (Number(additionalCost) === 0) {
                      setAdditionalCost("");
                    }
                  }}
                  onBlur={() => {
                    if (Number(additionalCost) === 0) {
                      setAdditionalCost(0);
                    }
                  }}
                  onChange={(event) => {
                    setAdditionalCost(event.target.value);
                    setAdditionalCostError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(additionalCost) || "",
                          Number(overlayData.AdditionalCost) || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"EquipmentCost"}
                  label="Equipment Cost"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  error={equipmentCostError}
                  value={equipmentCost}
                  onFocus={() => {
                    if (Number(equipmentCost) === 0) {
                      setEquipmentCost("");
                    }
                  }}
                  onBlur={() => {
                    if (Number(equipmentCost) === 0) {
                      setEquipmentCost(0);
                    }
                  }}
                  onChange={(event) => {
                    setEquipmentCost(event.target.value);
                    setEquipmentCostError(event.target.value === null);
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(equipmentCost) || "",
                          Number(overlayData.EquipmentCost) || "",
                          true,
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"TotalCost"}
                  label="Total Cost"
                  slotProps={{
                    input: {
                      readOnly: !disableAutomaticCalculationOfCharges,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  helperText={
                    disableAutomaticCalculationOfCharges
                      ? null
                      : totalCostHelperText
                  }
                  error={totalCostError}
                  value={totalCost}
                  onFocus={() => {
                    if (Number(totalCost) === 0) {
                      setTotalCost("");
                    }
                  }}
                  onBlur={() => {
                    if (Number(totalCost) === 0) {
                      setTotalCost(0);
                    }
                  }}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setTotalCost(nextValue);
                    setTotalCostError(nextValue === null);

                    if (disableAutomaticCalculationOfCharges) {
                      setLinguistPay(nextValue);
                    }
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(totalCost) || "",
                          Number(overlayData.TotalCost),
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id={"AccountCharge"}
                  label="Account Charge"
                  slotProps={{
                    input: {
                      readOnly: !disableAutomaticCalculationOfCharges,
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                  }}
                  helperText={
                    disableAutomaticCalculationOfCharges
                      ? null
                      : accountChargeHelperText
                  }
                  error={accountChargeError}
                  value={accountCharge}
                  onFocus={() => {
                    if (Number(accountCharge) === 0) {
                      setAccountCharge("");
                    }
                  }}
                  onBlur={() => {
                    if (Number(accountCharge) === 0) {
                      setAccountCharge(0);
                    }
                  }}
                  onChange={(event) => {
                    setAccountCharge(event.target.value);
                    setAccountChargeError(event.target.value === null);

                    const accountChargeValue = parseFloat(event.target.value);
                    const totalCostValue = parseFloat(totalCost);

                    // Prevent division by zero and handle invalid values
                    if (
                      !event.target.value ||
                      accountChargeValue === 0 ||
                      isNaN(accountChargeValue) ||
                      isNaN(totalCostValue)
                    ) {
                      setProfit(0);
                      setMargin(0);
                      return;
                    }

                    const prof = accountChargeValue - totalCostValue;

                    setProfit(prof.toFixed(2));
                    const calculatedMargin = (prof / accountChargeValue) * 100;
                    setMargin(
                      isNaN(calculatedMargin) || !isFinite(calculatedMargin)
                        ? 0
                        : calculatedMargin.toFixed(2),
                    );
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor: getBorderColor(
                          theme,
                          isNewAssignment,
                          Number(accountCharge) || "",
                          Number(overlayData.AccountCharge),
                        ),
                      },
                    },
                  }}
                />
              </Grid>

              {!completing ? (
                <Grid container size={{ xs: 12 }}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      disabled={isEditable ? !allowDetailsEditing : true}
                      size="small"
                      id={"Details"}
                      label="Details"
                      error={detailsError}
                      value={details}
                      onChange={(event) => {
                        setDetails(event.target.value);
                        setDetailsError(event.target.value === null);
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              details || "",
                              overlayData.Details,
                            ),
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={true}
                  size="small"
                  id={"Profit"}
                  label="Profit"
                  value={profit}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor:
                          profit === null || profit === ""
                            ? theme.palette.grey.main
                            : theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={true}
                  size="small"
                  id={"Margin"}
                  label="Margin"
                  value={margin}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">%</InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      py: 0,
                      "&.MuiInputBase-root fieldset": {
                        borderColor:
                          margin === null || margin === ""
                            ? theme.palette.grey.main
                            : theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    disabled={!isEditable}
                    size="small"
                    id={"The Note"}
                    label="Note"
                    multiline
                    value={note}
                    inputRef={noteInputRef}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setNote(nextValue);
                      handleNoteTagState(nextValue, event.target.selectionStart ?? nextValue.length);
                    }}
                    onKeyUp={(event) => {
                      const target = event.target;
                      const textValue = target && typeof target.value === "string" ? target.value : note;
                      const cursorPosition = target && typeof target.selectionStart === "number" ? target.selectionStart : textValue.length;
                      handleNoteTagState(textValue, cursorPosition);
                    }}
                    onClick={(event) => {
                      const target = event.target;
                      const textValue = target && typeof target.value === "string" ? target.value : note;
                      const cursorPosition = target && typeof target.selectionStart === "number" ? target.selectionStart : textValue.length;
                      handleNoteTagState(textValue, cursorPosition);
                    }}
                    onBlur={() => { setTimeout(() => { setIsMentionOpen(false); }, 120); }}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "8.5px 12px !important",
                      },
                      "& .MuiOutlinedInput-root": {
                        py: 0,
                        "&.MuiInputBase-root fieldset": {
                          borderColor:
                            note === null || note === ""
                              ? theme.palette.grey.main
                              : theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                  {isMentionOpen && (
                    <Popper
                      open={isMentionOpen}
                      anchorEl={noteInputRef.current}
                      placement="bottom-start"
                      sx={{ zIndex: 1800, width: noteInputRef.current?.offsetWidth || 360 }}
                    >
                      <Paper elevation={4} sx={{ mt: 0.5, maxHeight: 280, overflowY: "auto" }}>
                        {mentionTrigger === "#" && isMentionLoading ? (
                          <Box sx={{ p: 1.5, display: "flex", justifyContent: "center" }}>
                            <CircularProgress size={20} />
                          </Box>
                        ) : mentionTrigger === "#" ? (
                          serviceTagSuggestions.length > 0 ? (
                            <List dense disablePadding>
                              {serviceTagSuggestions.map((service) => (
                                <ListItemButton
                                  key={service.id}
                                  onMouseDown={(event) => { event.preventDefault(); handleSelectServiceTag(service); }}
                                >
                                  <ListItemText
                                    primary={`#${service.ProjectNumber}`}
                                    secondary={service.Details || "No details"}
                                  />
                                </ListItemButton>
                              ))}
                            </List>
                          ) : (
                            <Typography variant="body2" color="textSecondary" sx={{ p: 1.5 }}>No matching services</Typography>
                          )
                        ) : userTagSuggestions.length > 0 ? (
                          <List dense disablePadding>
                            {userTagSuggestions.map((member) => {
                              const mentionToken = getMemberMentionToken(member);
                              return (
                                <ListItemButton
                                  key={member.id}
                                  onMouseDown={(event) => { event.preventDefault(); handleSelectUserTag(member); }}
                                >
                                  <ListItemText
                                    primary={`@${mentionToken} · ${member.Name || "User"}`}
                                    secondary={member.Email || ""}
                                  />
                                </ListItemButton>
                              );
                            })}
                          </List>
                        ) : (
                          <Typography variant="body2" color="textSecondary" sx={{ p: 1.5 }}>No matching users</Typography>
                        )}
                      </Paper>
                    </Popper>
                  )}
                </Box>
              </Grid>

              {showRecurring && !completing ? (
                <Grid container size={{ xs: 12 }}>
                  <Grid size={{ xs: 12 }}>
                    <Divider />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      select
                      id={"RepeatEvery"}
                      label="Repeat Every"
                      error={repeatEveryError}
                      value={repeatEvery}
                      onChange={(event) => {
                        setRepeatEvery(event.target.value);
                        setRepeatEveryError(event.target.value === null);

                        handleRecursionChange(
                          date,
                          event.target.value,
                          period,
                          repeatUntilDate,
                          tempSelectedDays,
                          showRecurring,
                        );
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              repeatEvery || "",
                              " ",
                              true,
                            ),
                          },
                        },
                      }}
                    >
                      {repeatEveryList.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      disabled={!isEditable}
                      size="small"
                      select
                      id={"Period"}
                      label="Period"
                      error={periodError}
                      value={period}
                      onChange={(event) => {
                        if (!date || !date.isValid?.()) return;

                        setPeriod(event.target.value);
                        setPeriodError(event.target.value === null);

                        let selectedDays = [];

                        if (event.target.value === "Day")
                          selectedDays = [
                            "Mon",
                            "Tue",
                            "Wed",
                            "Thu",
                            "Fri",
                            "Sat",
                            "Sun",
                          ];

                        if (event.target.value === "Week")
                          selectedDays = [date.format("ddd")];

                        setTempSelectedDays(selectedDays);

                        const repeatUntilDateTemp = date.add(
                          1,
                          event.target.value === "Month" ? "year" : "month",
                        );

                        setRepeatUntilDate(repeatUntilDateTemp);

                        handleRecursionChange(
                          date,
                          repeatEvery,
                          event.target.value,
                          repeatUntilDateTemp,
                          selectedDays,
                          showRecurring,
                        );
                      }}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "8.5px 12px !important",
                        },
                        "& .MuiOutlinedInput-root": {
                          py: 0,
                          "&.MuiInputBase-root fieldset": {
                            borderColor: getBorderColor(
                              theme,
                              isNewAssignment,
                              period || "",
                              " ",
                              true,
                            ),
                          },
                        },
                      }}
                    >
                      {periodsList.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <MobileDatePicker
                      disabled={!isEditable}
                      id={"RepeatUntil"}
                      label="Repeat Until"
                      size="small"
                      format="dddd, MMMM DD, YYYY"
                      value={repeatUntilDate}
                      onChange={(newValue) => {
                        if (newValue && newValue.isValid()) {
                          setRepeatUntilDate(newValue);
                          setRepeatUntilDateError(false);

                          handleRecursionChange(
                            date,
                            repeatEvery,
                            period,
                            newValue,
                            tempSelectedDays,
                            showRecurring,
                          );
                        } else {
                          setRepeatUntilDateError(true);
                        }
                      }}
                      sx={{
                        ...getTextFieldBorderStyles(
                          getBorderColor(
                            theme,
                            isNewAssignment,
                            (repeatUntilDate &&
                              repeatUntilDate.format("YYYY-MM-DD")) ||
                              "",
                            "",
                            true,
                          ),
                        ),
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          error: dateError,
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <InlineGridSelector
                      disabled={
                        period === "Month" ||
                        (period === "Day" && repeatEvery !== 1)
                      } //
                      label="Days of the week"
                      items={daysList}
                      helperText={recursionStatusText}
                      selectedItems={tempSelectedDays}
                      onChange={handleDaysChange}
                      onSave={null}
                      itemsPerLine={7}
                      isMulti={true}
                      lockedItem={date?.format("ddd") || ""}
                    />
                  </Grid>
                </Grid>
              ) : null}

              <Grid container>
                <Grid size={{ xs: 12 }}>
                  <Divider />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography>Options</Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <HtmlTooltip
                    title={
                      <Box p=".5rem">
                        <Typography
                          color="inherit"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {
                            "Yes: Show the inputs for Actual Time, Scheduling, Preparation, and Total Duration."
                          }
                          {`\n\nNo: Show only the Start Time, End Time, and Duration.`}
                        </Typography>
                      </Box>
                    }
                  >
                    <FormControlLabel
                      control={<Checkbox />}
                      id={"ShowActualTimes"}
                      label="Show Actual Times"
                      checked={showActualTimes}
                      onChange={(event) => {
                        setShowActualTimes(event.target.checked);
                      }}
                    />
                  </HtmlTooltip>
                </Grid>

                {isNewAssignment ? (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <HtmlTooltip
                      title={
                        <Box p=".5rem">
                          <Typography
                            color="inherit"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {
                              "Yes: Show the options to make the assignment recurring."
                            }
                            {`\n\nNo: Hide the options to make the assignment recurring..`}
                          </Typography>
                        </Box>
                      }
                    >
                      <FormControlLabel
                        control={<Checkbox />}
                        id={"showRecurring"}
                        label="Recurring Assignment"
                        checked={showRecurring}
                        onChange={(event) => {
                          setShowRecurring(event.target.checked);

                          if (event.target.checked)
                            handleRecursionChange(
                              date,
                              repeatEvery,
                              period,
                              repeatUntilDate,
                              tempSelectedDays,
                              event.target.checked,
                            );
                        }}
                      />
                    </HtmlTooltip>
                  </Grid>
                ) : null}

                <Grid size={{ xs: 12, md: 6 }}>
                  <HtmlTooltip
                    title={
                      <Box p=".5rem">
                        <Typography
                          color="inherit"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {
                            "Yes: Linguists list will have all Linguists (Current, Former, Prospect...)"
                          }
                          {`\n\nNo: Linguists list will only show Current Linguists.`}
                        </Typography>
                      </Box>
                    }
                  >
                    <FormControlLabel
                      control={<Checkbox />}
                      id={"ShowAllLinguists"}
                      label="Show all Linguists (Not only the current ones)"
                      checked={showAllLinguists}
                      onChange={(event) => {
                        setShowAllLinguists(event.target.checked);
                      }}
                    />
                  </HtmlTooltip>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <HtmlTooltip
                    title={
                      <Box p=".5rem">
                        <Typography
                          color="inherit"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {
                            "Yes: Only show Linguists who speak the selected Language."
                          }
                          {`\n\nNo: Show All Linguists.`}
                        </Typography>
                      </Box>
                    }
                  >
                    <FormControlLabel
                      control={<Checkbox />}
                      id={"FilterLinguistsByLanguage"}
                      label="Filter Linguists By Language"
                      checked={filterLingsByLangs}
                      onChange={(event) => {
                        setFilterLingsByLangs(event.target.checked);
                      }}
                    />
                  </HtmlTooltip>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <HtmlTooltip
                    title={
                      <Box p=".5rem">
                        <Typography
                          color="inherit"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {"Yes: Allow the editing of the details."}
                          {`\n\nNo: Details will be not-editable, and will be filled according to Name, Requestor, and Language.`}
                        </Typography>
                      </Box>
                    }
                  >
                    <FormControlLabel
                      control={<Checkbox />}
                      id={"AllowDetailsEditing"}
                      label="Allow Manual Editing of Details"
                      checked={allowDetailsEditing}
                      onChange={(event) => {
                        setAllowDetailsEditing(event.target.checked);

                        if (!event.target.checked)
                          fillDetails(
                            requestor.Name,
                            name,
                            language.Name,
                            interpretationType,
                          );
                      }}
                    />
                  </HtmlTooltip>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <HtmlTooltip
                    title={
                      <Box p=".5rem">
                        <Typography
                          color="inherit"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {"Yes: Allow the editing of the Total Duration."}
                          {`\n\nNo: Total Duration will be not-editable, and will be filled according to Start/End Time, and Actual Start/End.`}
                        </Typography>
                      </Box>
                    }
                  >
                    <FormControlLabel
                      control={<Checkbox />}
                      id={"disableAutomaticCalculationOfCharges"}
                      label="Disable Automatic Calculation of Charges"
                      checked={disableAutomaticCalculationOfCharges}
                      onChange={(event) => {
                        setDisableAutomaticCalculationOfCharges(
                          event.target.checked,
                        );
                      }}
                    />
                  </HtmlTooltip>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <HtmlTooltip
                    title={
                      <Box p=".5rem">
                        <Typography
                          color="inherit"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {
                            "Yes: Automatic calculation of Linguist Pay, Total Cost, and Account Charge will be disabled."
                          }
                          {`\n\nNo: Automatic calculation of Linguist Pay, Total Cost, and Account Charge will be enabled.`}
                        </Typography>
                      </Box>
                    }
                  >
                    <FormControlLabel
                      control={<Checkbox />}
                      id={"allowTotalDurationEditing"}
                      label="Allow Manual Editing of Total Duration"
                      checked={allowTotalDurationEditing}
                      onChange={(event) => {
                        setAllowTotalDurationEditing(event.target.checked);

                        if (!event.target.checked) {
                          const tempTotalDuration = sumDurationValues([
                            duration && actualDuration
                              ? Math.max(
                                  actualDuration.hour() * 60 +
                                    actualDuration.minute(),
                                  duration.hour() * 60 + duration.minute(),
                                )
                              : duration || actualDuration || 0,
                            schedulingTime,
                            preparationTime,
                          ]);

                          setTotalDuration(tempTotalDuration);
                        }
                        //fillDetails(requestor.Name, name, language.Name);
                      }}
                    />
                  </HtmlTooltip>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <HtmlTooltip
                    title={
                      <Box p=".5rem">
                        <Typography
                          color="inherit"
                          style={{ whiteSpace: "pre-line" }}
                        >
                          {
                            "Yes: Allows the creation of assignments that are less than 1 hour long (Scheduling, or calling assignments)."
                          }
                          {`\n\nNo: Assignments will be at least 1 hour long.`}
                        </Typography>
                      </Box>
                    }
                  >
                    <FormControlLabel
                      control={<Checkbox />}
                      id={"AllowShortAssignments"}
                      label="Allow Short Assignments (< 1 hour)"
                      checked={allowShortAssignments}
                      onChange={(event) => {
                        setAllowShortAssignments(event.target.checked);
                      }}
                    />
                  </HtmlTooltip>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Holiday indicator bar */}
        <Box
          sx={{
            width: "100%",
            height: selectedDateHoliday ? "32px" : "0px",
            opacity: selectedDateHoliday ? 1 : 0,
            transition: selectedDateHoliday
              ? "height 0.3s ease-in-out, opacity 0.3s ease-in-out"
              : "opacity 0.2s ease-in-out, height 0.3s ease-in-out 0.1s",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: theme.palette.background.info,
            borderTop: "none",
            borderRadius: "0 0 12px 12px",
            borderBottom: selectedDateHoliday
              ? `1px solid ${theme.palette.background.info}`
              : "none",
          }}
        >
          {selectedDateHoliday && (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.primary.text,
                fontWeight: 600,
                fontSize: "0.875rem",
                textAlign: "center",
                transition: "opacity 0.3s ease-in-out",
                opacity: selectedDateHoliday ? 1 : 0,
              }}
            >
              {selectedDateHoliday.Name}
              {selectedDateHoliday.Description && (
                <span
                  style={{
                    fontWeight: 400,
                    fontStyle: "italic",
                    marginLeft: "8px",
                    color: theme.palette.text.secondary,
                  }}
                >
                  ({selectedDateHoliday.Description})
                </span>
              )}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: ".75rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              width: "50%",
              flexDirection: "row",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color:
                  progress < 50
                    ? theme.palette.error[600]
                    : progress < 100
                      ? theme.palette.warning[500]
                      : theme.palette.primary.main,
              }}
            >
              {progressMessage}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "flex-end",
              width: "50%",
              flexDirection: "row",
              gap: isMobile ? 1 : 0,
            }}
          >
            {isMobile ? (
              <>
                <Tooltip title="Discard">
                  <IconButton
                    color="primary"
                    onClick={() => {
                      releaseLock();
                      setOverlayName("");
                    }}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.background.default,
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHover,
                      },
                    }}
                  >
                    <DisabledByDefaultRoundedIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Save">
                  <IconButton
                    onClick={attemptSave}
                    disabled={isSavingChanges || isLocked}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "12px",
                      color: "white",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.primary.buttonHoverSelected,
                      },
                      "&:disabled": {
                        backgroundColor: theme.palette.grey[400],
                        color: theme.palette.grey[200],
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  color="primary"
                  variant="outlined"
                  size="large"
                  endIcon={<DisabledByDefaultRoundedIcon />}
                  onClick={() => {
                    releaseLock();
                    setOverlayName("");
                  }}
                  sx={{
                    borderRadius: "12px",
                  }}
                >
                  Discard
                </Button>

                <Box p="0 .5rem" />

                <Button
                  loading={isSavingChanges}
                  disabled={isSavingChanges || isLocked}
                  color="primary"
                  variant="contained"
                  size="large"
                  endIcon={<SaveRoundedIcon />}
                  type="submit"
                  onClick={attemptSave}
                  sx={{
                    borderRadius: "12px",
                  }}
                >
                  Save
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Paid Assignment Warning Dialog */}
      <Dialog
        open={showPaidWarning}
        onClose={handlePaidWarningCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
            fontWeight: "bold",
          }}
        >
          ⚠️ Paid Assignment Warning
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText
            sx={{
              color: theme.palette.text.primary,
              whiteSpace: "pre-line",
              fontWeight: 500,
            }}
          >
            {paidWarningMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handlePaidWarningCancel}
            color="inherit"
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePaidWarningConfirm}
            color="warning"
            variant="contained"
            sx={{ borderRadius: "8px" }}
            autoFocus
          >
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </Box>
  );
};

export default InterpretationNewEdit;
